import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { EditorContent, ReactNodeViewRenderer, useEditor, useEditorState, type Editor } from '@tiptap/react';
import { DOMParser as PMDOMParser, type Node as PMNode } from '@tiptap/pm/model';
import { Selection } from '@tiptap/pm/state';
import { undoDepth } from '@tiptap/pm/history';
import StarterKit from '@tiptap/starter-kit';
import { BackgroundColor, Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table, TableHeader, TableRow } from '@tiptap/extension-table';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { TooltipProvider } from '@/components/ui/tooltip';
import { toast } from './avisos';
import {
  ESTILOS, ESTILO_PADRAO, PREFIXO_PERSONALIZADO, ajustesQueValem, definirRegistroDeEstilos, diferencaDeFormato, estiloPersonalizado, fmtNum, formatoDoPronto,
  nomeDoEstilo, pilhaDaFonte, type AjustesDosEstilosProntos, type AlcanceDoModelo, type EstiloPersonalizado, type FormatoParagrafo, type NomeEstilo,
} from './estilos';
import { PainelCriarEstilo, PainelGerenciarEstilos } from './ui/painelEstilos';
import { htmlDeSaida } from './saida';
import { baixarBlob, compartilharArquivo, detalhesDoTexto, imprimirFolha, linkDeEmail, nomeDeArquivo, paginaHtml, podeCompartilharArquivo, textoPuro, type FormatoArquivo, type FormatoGerado } from './arquivo';
import { DOCUMENTO_PADRAO, larguraDoPapel, mesmaPagina, paginaDoDocumento, type DocumentoForense, type PaginaPadrao, type Timbrado } from './documento';
import { FormatoParagrafoExt, formatoNoCursor } from './extensoes/formatoParagrafo';
import { aplicarAjusteDaIA, CHAVE_DESTAQUE_IA, DestaqueIA, htmlParaIA, limparDestaqueDaIA, type ResultadoDoAjuste } from './ajusteDaIA';
import { ColagemSegura, htmlDaIANosEstilos, limparHtmlColado, NotaRodape, NumeracaoJuridica, Quebra } from './extensoes/pecaForense';
import { CelulaComFormula, celulaNoCursor, referenciaNoCursor } from './extensoes/formulas';
import { Busca, Ortografia, SugestoesIA, buscaNoEstado, sugestoesNoEstado, CHAVE_ORTO, CHAVE_IA, type ProvedorOrtografia, type Revisor } from './extensoes/revisao';
import { AtalhosWord, type AcaoDeTela } from './extensoes/atalhos';
import {
  ORIGEM_IA, alteracoesDaPessoa, compararParaTela, houveAlteracaoDoUsuario, novaBaseDaIA,
  type AlteracoesDoUsuario, type Autoria, type OrigemVersao, type VersaoDoTexto,
} from './versoes';
import { ConteudoVersoes, FolhaComparacao, quandoDaVersao } from './ui/painelVersoes';
import type { ChangeSet } from '@tiptap/pm/changeset';
import { formatDateTime } from './formatos';
import { INTERVALOS_SALVAMENTO, Abas, BarraArquivo, BarraInicio, BarraParagrafo, BarraInserir, BarraLayout, BarraRevisar, BarraTabela, type Aba, type AcoesEditor, type EstadoEditor, type SituacaoSalvamento } from './ui/barras';
import { BarraCelular, Gavetas, type AcaoMais, type Gaveta } from './ui/celular';
import { Cabecalho, FolhaContext, PX_CM as PX_POR_CM, QuebraView, Regua, Rodape, totalDePaginas, type ContextoFolha } from './ui/pagina';
import { Botao, BotaoNu } from './ui/comum';
import { Maximize2, Minimize2, MoveHorizontal, ZoomIn, ZoomOut } from 'lucide-react';
import { ConteudoBusca, Lateral, PainelMargens, PainelParagrafo, PainelTimbrado } from './ui/paineis';
import { PainelFormatacao } from './ui/painelFormatacao';
import type { ModoDoTimbrado } from './ui/editorDeTimbrado';
import { timbradoDoArquivo, type ExtracaoDeArquivo } from './formatacao';
import { BarraRevisaoIA, CartaoSugestaoIA, focarCartao } from './ui/revisaoIA';
import './editor-forense.css';

// Com o app, só os modos de timbrado que o servidor guarda (texto e logo, faixas).
const MODOS_DO_APP: ModoDoTimbrado[] = ['texto', 'imagem'];

/**
 * EditorForense — editor de texto para peças jurídicas.
 *
 * Seis abas no computador (Início, Parágrafo, Inserir, Tabela, Layout,
 * Revisar) e uma barra de uma linha no celular. Estilos prontos de peça,
 * régua, painel Parágrafo, tabelas com fórmulas, cabeçalho e rodapé com
 * papel timbrado, numeração de páginas e de folhas, notas de rodapé,
 * ortografia (navegador + dicionário embutido), versões e revisão por IA —
 * com os atalhos do Word em português na tooltip de cada botão.
 *
 * O TEXTO (`valor`) e o DOCUMENTO (`documento`: papel, margens, timbrado,
 * numeração) são separados: o corpo é o que se cola num e-mail ou num campo
 * de petição; o documento é como a peça sai impressa.
 *
 * A revisão por IA chega por função do app (`revisarIA`) — o editor não
 * embute URL nem segredo. Sem a função, o botão não aparece.
 *
 * Paginação AUTOMÁTICA (quebrar a folha sozinha ao encher) é a segunda etapa;
 * nesta, as páginas são as das quebras manuais (Ctrl+Enter).
 */
/** Uma aba que o app acrescenta ao editor. */
export interface AbaDoApp {
  chave: string;
  nome: string;
  barra: ReactNode;
}

export interface EditorForenseHandle {
  /** Abre (e mostra) a barra de uma aba do app. */
  abrirAbaDoApp: (chave: string) => void;
  appendText: (texto: string) => void;
  focus: () => void;
  editor: Editor | null;
  /**
   * A IA acabou de ler o texto: o que a pessoa mudar daqui em diante é o que
   * `alteracoesDesdeIA` devolve e o que faz `onAlteracaoDoUsuario(true)`.
   */
  marcarVersaoVistaPelaIA: () => void;
  /** O que a pessoa mudou à mão desde a última `marcarVersaoVistaPelaIA` (escritas da IA não entram). */
  alteracoesDesdeIA: () => AlteracoesDoUsuario;
  /**
   * A IA escreve no texto: `tudo` troca a peça (guardando antes uma versão),
   * `fim` acrescenta ao final, `selecao` troca o trecho selecionado (ou insere no cursor).
   * Não conta como alteração da pessoa.
   */
  escreverPelaIA: (html: string, onde?: 'tudo' | 'fim' | 'selecao') => void;
  /**
   * A IA está escrevendo a minuta em pedaços (streaming): o salvamento
   * automático espera ela terminar (`false`) — senão a pausa entre dois pedaços
   * gravaria a minuta pela metade. Se o fim nunca vier, um minuto sem escrita
   * da IA solta o salvamento.
   */
  segurarSalvamento: (segurar: boolean) => void;
  /**
   * AJUSTE da IA aplicado parágrafo a parágrafo (`ajusteDaIA.ts`): o que não mudou
   * fica como está, com estilo, notas e marca-texto; os alterados ficam destacados.
   * Guarda antes a versão "Antes da IA escrever". `base` é o `editor.state.doc` do
   * envio (o texto que a IA leu): o que a pessoa mexeu depois fica como ela deixou.
   */
  ajustarPelaIA: (html: string, base?: unknown) => ResultadoDoAjuste;
  /** O texto vigente no HTML simples que vai à IA (`<minuta_atual>`), ou null se vazio. */
  textoParaIA: () => string | null;
  /** Tira o destaque dos parágrafos que a IA alterou. */
  limparDestaquesDaIA: () => void;
  /** Guarda uma versão agora (ex.: antes de o app trocar o texto por fora). */
  guardarVersao: (rotulo?: string) => void;
}

export interface EditorForenseProps {
  /** HTML do corpo. */
  valor: string;
  onChange: (html: string) => void;
  /** Papel, margens, timbrado e numeração. Sem ele, ABNT em A4, sem timbrado. */
  documento?: DocumentoForense;
  onDocumentoChange?: (d: DocumentoForense) => void;
  /** Timbrados do escritório (o app guarda). */
  timbrados?: Timbrado[];
  /** Chamado quando a pessoa cadastra ou edita um timbrado. */
  /** Pode devolver a promessa da gravação: o "Desfazer" espera por ela. */
  onTimbradoSalvo?: (t: Timbrado) => void | Promise<void>;
  /** A página das peças novas guardada pelo app (`null`: a do app, A4 ABNT). */
  paginaPadrao?: PaginaPadrao | null;
  /** Guarda a página das peças novas (`null` volta à do app). Sem ela, o editor não oferece. */
  onPaginaPadrao?: (p: PaginaPadrao | null) => void | Promise<void>;
  placeholder?: string;
  /** Dicionário embutido (ver `criarOrtografiaPtBr`). Sem ele, só o corretor do navegador. */
  ortografia?: ProvedorOrtografia | null;
  dicionarioPessoal?: string[];
  onDicionarioPessoalChange?: (palavras: string[]) => void;
  /** Revisão por IA: recebe o texto e devolve as sugestões. Sem ela, o botão não aparece. */
  revisarIA?: Revisor;
  /** Créditos que revisar N caracteres debita — mostrados antes de revisar. */
  creditosIA?: (caracteres: number) => number;
  /**
   * `completo` — o editor de peça.
   * `simples` — só a barra de texto, sem folha (corpo de e-mail, observação).
   */
  modo?: 'completo' | 'simples';
  somenteLeitura?: boolean;
  /** Liga o botão de link; devolve a URL (ou null para desistir). */
  onRequestLink?: () => string | null | Promise<string | null>;
  /** Abaixo desta largura (px) o editor usa a barra do celular. Default 640. */
  larguraCelular?: number;
  /**
   * Barras de ferramentas RECOLHIDAS: só a linha das abas aparece; a barra de
   * uma aba abre quando se clica nela e fecha num segundo clique na mesma aba.
   * Default `true` (a folha ganha a altura que a barra ocuparia). `false` =
   * barra sempre aberta.
   */
  barrasRecolhidas?: boolean;
  /** Altura mínima da área de texto no modo simples. */
  alturaMinima?: number;
  /**
   * Controles do app na ponta da barra (ex.: "inserir variável" num modelo).
   * Recebe `inserir`, que põe o texto no cursor. Aparece em todas as abas.
   */
  extrasBarra?: (inserir: (texto: string) => void) => ReactNode;
  /** Aviso que flutua sobre a folha, rente à barra de status, sem empurrar o texto. */
  aviso?: ReactNode;
  /**
   * Abas do app depois de Revisar (ex.: o Protocolo nos autos). A barra de cada uma
   * fica MONTADA mesmo com a aba fechada — ela guarda estado (o que já se escolheu,
   * um pedido em curso) —, só escondida. No celular, sem abas, fica sempre à vista.
   */
  abasDoApp?: AbaDoApp[];
  /** A aba do app que nasce aberta (o app a lembra entre remontagens do editor). */
  abaDoAppInicial?: string | null;
  /** Avisa qual aba do app está aberta (`null` = nenhuma, ou a barra recolhida). */
  onAbaDoApp?: (chave: string | null) => void;
  /** Nome dos arquivos da aba Arquivo (baixar, imprimir, compartilhar), sem a extensão. Default "Peça". */
  nomeArquivo?: string;
  /**
   * Gera o DOCX e o PDF da peça (no servidor). Liga "Word" e "PDF" em Baixar e
   * em Compartilhar; sem ele, só a página da web (.html) e a impressão. `null`
   * = o app já avisou o motivo (não baixa nada).
   */
  onGerarArquivo?: (formato: FormatoGerado) => Promise<Blob | null>;
  /**
   * Mede a formatação de uma petição do advogado (Word ou PDF). Liga "Formatação
   * do texto" na aba Arquivo; rejeita com a mensagem que o painel mostra.
   */
  extrairFormatacao?: (arquivo: File) => Promise<ExtracaoDeArquivo>;
  /**
   * As mesmas ações dos `extrasBarra` como itens do menu "Mais" do celular. Com
   * elas, no telefone os extras saem da barra (lá tomavam uma linha inteira).
   */
  acoesMais?: AcaoMais[];
  /**
   * Liga o botão Salvar (Shift+F12), o salvamento automático e o aviso ao sair
   * com alterações não salvas. Recebe o HTML de saída; pode ser assíncrono —
   * se rejeitar, a barra mostra "Não salvou" e oferece tentar de novo.
   */
  onSalvar?: (html: string) => void | Promise<void>;
  /**
   * Estilos do escritório: os criados (`u:<id>` no HTML) e os ajustes sobre os
   * prontos. O app guarda; com `onSalvarEstilo` o menu ganha Criar, Atualizar e
   * Gerenciar. Mudar um estilo redesenha os parágrafos escritos nele.
   */
  estilosPersonalizados?: EstiloPersonalizado[];
  ajustesDosEstilosProntos?: AjustesDosEstilosProntos;
  onSalvarEstilo?: (e: EstiloPersonalizado) => void | Promise<void>;
  onExcluirEstilo?: (id: string) => void | Promise<void>;
  /** O ajuste de um pronto; `alcance` (padrão: o do escritório) diz de quem é. */
  onAjustarEstiloPronto?: (nome: NomeEstilo, ajustes: Partial<FormatoParagrafo> | null, alcance?: AlcanceDoModelo) => void | Promise<void>;
  /**
   * Os ajustes dos prontos "só para mim" de quem escreve, guardados pelo app: a
   * base do "só para mim" da formatação. Na folha vale o que a PEÇA guarda
   * (`documento.ajustesProntos`), com o do escritório por cima.
   */
  ajustesPessoaisDosEstilosProntos?: AjustesDosEstilosProntos;
  /**
   * Onde o "como modelo" da formatação pode guardar o Corpo da peça: só para quem
   * escreve e/ou para todo o escritório. Sem ela, todo o escritório quando pode
   * mudar os estilos dele.
   */
  alcancesDoModelo?: AlcanceDoModelo[];
  /** O Corpo da peça do escritório vence o da peça (obrigatório, o padrão) ou só vale onde ela não ajustou (sugerido). */
  escritorioPrevalece?: boolean;
  /** Título da seção no menu. Default "Estilos do escritório". */
  rotuloEstilosPersonalizados?: string;
  /** Pode criar e mudar estilos que valem para todos (senão, só "para mim"). Default true. */
  podeEditarEstilosDoEscritorio?: boolean;
  /** Pausa (s) depois da última alteração para salvar sozinho, até a pessoa escolher outra. 0 = desligado. Default 3. */
  salvamentoAutomaticoPadrao?: number;
  /** Onde fica salvo, dito na barra: "neste navegador", "no servidor". */
  ondeSalva?: string;
  /**
   * Versões da peça (painel Versões, na aba Revisar). O app guarda a lista; o
   * editor cria uma a cada salvamento (o automático, no máximo uma a cada 10
   * min), antes de a IA trocar o texto e antes de voltar a uma versão. Sem
   * `onVersao`, as versões ficam só na memória desta visita.
   */
  versoes?: VersaoDoTexto[];
  onVersao?: (v: VersaoDoTexto) => void | Promise<void>;
  onExcluirVersao?: (id: string) => void | Promise<void>;
  /**
   * Texto de uma versão listada sem o `html` — chamado ao comparar com ela ou
   * voltar a ela. A lista do app pode trazer só os metadados (uma peça longa
   * com cem versões não precisa vir inteira a cada abertura).
   */
  carregarVersao?: (id: string) => Promise<string>;
  /**
   * Avisado (com folga de ~1,5 s) quando a pessoa mexe no texto depois da
   * última leitura da IA (`marcarVersaoVistaPelaIA`) — e de novo com `false`
   * quando a IA lê outra vez ou a pessoa desfaz o que mudou.
   */
  onAlteracaoDoUsuario?: (houve: boolean) => void;
  /**
   * Prefixo das preferências guardadas no navegador (zoom da folha e intervalo
   * do salvamento automático). O app passa uma chave já escopada pelo usuário;
   * sem ela, nada é lido nem gravado e as escolhas valem só nesta visita.
   */
  chaveArmazenamento?: string | null;
  /**
   * Tela cheia do APP no lugar da do editor (no Peticionar, o editor e o
   * assistente juntos): o botão do editor passa a comandá-la, e o editor não
   * cobre a janela por conta própria.
   */
  telaCheiaDoApp?: { ativa: boolean; alternar: () => void; descricao?: string };
  /**
   * Chamado a cada `valor` vindo de fora (rascunho, minuta, modelo) que o editor
   * aceita — também quando ele já está como o editor o escreveria: devolve o HTML
   * como o editor o emite, com o estilo em linha que o PDF usa. É a confirmação de
   * que o texto do app é o do editor; sem ela, o app guardaria o valor de fora até
   * a primeira tecla.
   */
  onValorNormalizado?: (html: string) => void;
  className?: string;
  style?: CSSProperties;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;

/** Lê uma preferência do navegador; sem chave (ou sem armazenamento), null. */
function lerPreferencia(chave: string | null): string | null {
  if (!chave) return null;
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}
function gravarPreferencia(chave: string | null, valor: string): void {
  if (!chave) return;
  try { localStorage.setItem(chave, valor); } catch { /* vale só nesta visita */ }
}

/** Zoom guardado no navegador (vale entre visitas); 100% se não houver. */
function lerZoom(chave: string | null): number {
  const bruto = lerPreferencia(chave);
  const v = bruto === null ? NaN : Number(bruto);
  return v >= ZOOM_MIN && v <= ZOOM_MAX ? v : 1;
}

const SEM_ESTILOS: EstiloPersonalizado[] = [];
const SEM_AJUSTES: AjustesDosEstilosProntos = {};

/** A escolha da pessoa vale entre visitas; sem escolha, o padrão do app. */
function lerIntervalo(chave: string | null, padrao: number): number {
  const v = lerPreferencia(chave);
  // Uma escolha que não existe mais (os 60 s e 5 min do salvamento por relógio) volta ao padrão.
  if (v !== null && INTERVALOS_SALVAMENTO.some((o) => o.segundos === Number(v))) return Number(v);
  return padrao;
}

/** O salvamento automático guarda versão no máximo a cada 10 min; o botão, sempre. */
const INTERVALO_VERSAO_AUTOMATICA = 10 * 60_000;
/** Quem digita sem parar não fica sem salvar: no máximo isto depois da 1ª alteração. */
const ESPERA_MAXIMA_SALVAMENTO = 60_000;
const SEM_VERSOES: VersaoDoTexto[] = [];

const contarPalavras = (t: string) => (t.match(/[A-Za-zÀ-ÿ0-9]+/g) ?? []).length;

export const EditorForense = forwardRef<EditorForenseHandle, EditorForenseProps>(function EditorForense(props, ref) {
  const {
    valor, onChange, placeholder = 'Redija a peça…', ortografia = null, revisarIA, creditosIA,
    modo = 'completo', somenteLeitura = false, larguraCelular = 640, alturaMinima = 240,
    barrasRecolhidas = true,
    className, style, extrasBarra, aviso, acoesMais, onSalvar, salvamentoAutomaticoPadrao = 3, ondeSalva,
    estilosPersonalizados = SEM_ESTILOS, ajustesDosEstilosProntos = SEM_AJUSTES, onSalvarEstilo, onExcluirEstilo, onAjustarEstiloPronto,
    ajustesPessoaisDosEstilosProntos = SEM_AJUSTES, escritorioPrevalece = true,
    rotuloEstilosPersonalizados = 'Estilos do escritório', podeEditarEstilosDoEscritorio = true,
    onVersao, onExcluirVersao, onAlteracaoDoUsuario, chaveArmazenamento = null, telaCheiaDoApp, onValorNormalizado,
    extrairFormatacao,
  } = props;
  const onValorNormalizadoRef = useRef(onValorNormalizado);
  useLayoutEffect(() => { onValorNormalizadoRef.current = onValorNormalizado; });
  // O último valor que o editor emitiu ou confirmou: o que volta do app igual não
  // precisa de nova confirmação.
  const confirmadoRef = useRef<string | null>(null);
  const chaveZoom = chaveArmazenamento ? `${chaveArmazenamento}:zoom` : null;
  const chaveIntervalo = chaveArmazenamento ? `${chaveArmazenamento}:salvamento` : null;

  /* ---------- Zoom da folha (como no Word: na barra de baixo) ---------- */
  const [zoom, setZoom] = useState(() => lerZoom(chaveZoom));
  const mesaRef = useRef<HTMLDivElement | null>(null);
  const guardarZoom = useCallback((z: number) => {
    const v = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 10) / 10));
    setZoom(v);
    gravarPreferencia(chaveZoom, String(v));
  }, [chaveZoom]);
  // A mesa medida: a largura (para "Ajustar à largura" saber se já está ajustada,
  // inclusive depois de recarregar com o zoom guardado) e, para a régua, a rolagem
  // lateral (dividida pelo `zoom` da régua, que amplia também o deslocamento) e a
  // largura da barra de rolagem vertical (a folha centraliza sem ela).
  const [larguraMesa, setLarguraMesa] = useState(0);
  const zoomRef = useRef(zoom);
  const medirMesa = useCallback(() => {
    const m = mesaRef.current;
    if (!m) return;
    setLarguraMesa(m.clientWidth);
    const raizEl = m.closest('.cm-ef') as HTMLElement | null;
    raizEl?.style.setProperty('--cm-ef-rolagem-x', `${m.scrollLeft / zoomRef.current}px`);
    raizEl?.style.setProperty('--cm-ef-barra-y', `${m.offsetWidth - m.clientWidth}px`);
  }, []);
  const observadorMesa = useRef<ResizeObserver | null>(null);
  const refMesa = useCallback((el: HTMLDivElement | null) => {
    mesaRef.current = el;
    observadorMesa.current?.disconnect();
    if (!el) return;
    medirMesa();
    if (typeof ResizeObserver === 'undefined') return;
    observadorMesa.current = new ResizeObserver(medirMesa);
    observadorMesa.current.observe(el);
  }, [medirMesa]);
  // Mudar o zoom não rola a mesa: a régua precisa da conta nova mesmo assim.
  useLayoutEffect(() => {
    zoomRef.current = zoom;
    medirMesa();
  }, [zoom, medirMesa]);

  /* ---------- Salvamento (botão, automático, aviso ao sair) ---------- */
  const [situacaoSalvo, setSituacaoSalvo] = useState<SituacaoSalvamento>('novo');
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [intervalo, setIntervalo] = useState(() => lerIntervalo(chaveIntervalo, salvamentoAutomaticoPadrao));
  const versaoRef = useRef(0);
  const sujoRef = useRef(false);
  const salvandoRef = useRef(false);
  const onSalvarRef = useRef(onSalvar);
  onSalvarRef.current = onSalvar;
  const marcarSujoRef = useRef(() => {});
  marcarSujoRef.current = () => {
    if (!onSalvarRef.current) return;
    versaoRef.current += 1;
    sujoRef.current = true;
    setSituacaoSalvo((s) => (s === 'salvando' ? s : 'sujo'));
    agendarRef.current();
  };
  // O salvamento automático é por pausa: `intervalo` segundos depois da última
  // alteração (e nunca mais que ESPERA_MAXIMA_SALVAMENTO depois da primeira).
  const intervaloRef = useRef(intervalo);
  intervaloRef.current = intervalo;
  const pausaRef = useRef<number | undefined>(undefined);
  const primeiraAlteracaoRef = useRef<number | null>(null);
  const seguradoRef = useRef(false);
  const agendarRef = useRef(() => {});
  agendarRef.current = () => {
    window.clearTimeout(pausaRef.current);
    if (!sujoRef.current || intervaloRef.current <= 0) return;
    if (seguradoRef.current) {
      // A IA escrevendo: cada pedaço recomeça a espera; sem pedaço por um minuto, solta.
      pausaRef.current = window.setTimeout(() => { seguradoRef.current = false; agendarRef.current(); }, ESPERA_MAXIMA_SALVAMENTO);
      return;
    }
    primeiraAlteracaoRef.current ??= Date.now();
    const limite = primeiraAlteracaoRef.current + ESPERA_MAXIMA_SALVAMENTO - Date.now();
    pausaRef.current = window.setTimeout(
      () => { if (sujoRef.current) salvarRef.current(true); },
      Math.max(0, Math.min(intervaloRef.current * 1000, limite)),
    );
  };
  const editorRef = useRef<Editor | null>(null);
  const salvarRef = useRef((_automatico?: boolean) => {});
  salvarRef.current = (automatico = false) => {
    const fn = onSalvarRef.current;
    const ed = editorRef.current;
    if (!fn || !ed || salvandoRef.current) return;
    const versao = versaoRef.current;
    const h = ed.getHTML();
    salvandoRef.current = true;
    window.clearTimeout(pausaRef.current);
    primeiraAlteracaoRef.current = null;
    setSituacaoSalvo('salvando');
    void Promise.resolve()
      .then(() => fn(h === '<p></p>' ? '' : htmlDeSaida(h)))
      .then(() => {
        // Cada salvamento vira versão; o automático, no máximo a cada 10 min.
        const ult = ultimaVersaoRef.current;
        if (!automatico || !ult || Date.now() - new Date(ult.criadaEm).getTime() >= INTERVALO_VERSAO_AUTOMATICA) {
          guardarVersaoRef.current(automatico ? 'automatico' : 'salvamento', undefined, h);
        }
        // O que se digitou durante o salvamento continua por salvar.
        sujoRef.current = versaoRef.current !== versao;
        setSalvoEm(new Date());
        setSituacaoSalvo(sujoRef.current ? 'sujo' : 'salvo');
      })
      .catch(() => setSituacaoSalvo('erro'))
      .finally(() => {
        salvandoRef.current = false;
        // O que se digitou durante o salvamento: a próxima pausa o salva.
        agendarRef.current();
      });
  };
  const definirIntervalo = useCallback((s: number) => {
    setIntervalo(s);
    gravarPreferencia(chaveIntervalo, String(s));
  }, [chaveIntervalo]);
  useEffect(() => {
    // Trocou o intervalo com alteração pendente: vale o novo.
    agendarRef.current();
    return () => window.clearTimeout(pausaRef.current);
  }, [intervalo]);
  useEffect(() => {
    if (!onSalvar) return;
    const aoSair = (e: BeforeUnloadEvent) => { if (sujoRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', aoSair);
    return () => window.removeEventListener('beforeunload', aoSair);
  }, [onSalvar]);
  const horaSalvo = salvoEm ? formatDateTime(salvoEm.toISOString()).split(' às ')[1] : '';
  const onde = ondeSalva ? ` ${ondeSalva}` : '';
  // Curto para caber na largura fixa da barra de estado (o meio da barra não pula);
  // o detalhe vai na dica.
  const textoSalvamento =
    situacaoSalvo === 'salvando' ? 'Salvando…'
      : situacaoSalvo === 'erro' ? 'Não salvou — tente de novo'
        : situacaoSalvo === 'sujo' ? (intervalo > 0 ? 'Alterações ainda não salvas' : 'Alterações não salvas')
          : situacaoSalvo === 'salvo' ? `Salvo às ${horaSalvo}${onde}`
            // Recém-aberta: nada a salvar (o que se abriu já está guardado, ou a peça está em branco).
            : 'Sem alterações';
  const dicaSalvamento =
    situacaoSalvo === 'erro' ? 'Não salvou. Clique em Salvar para tentar de novo.'
      : situacaoSalvo === 'sujo' && intervalo <= 0 ? 'O salvamento automático está desligado.'
        : situacaoSalvo === 'novo'
          ? `Nada mudou desde que a peça foi aberta. ${intervalo > 0 ? 'Ao editar, ela se salva sozinha.' : 'O salvamento automático está desligado.'}`
          : textoSalvamento;

  /* ---------- Estados controlados ou internos ---------- */
  const [docInterno, setDocInterno] = useState<DocumentoForense>(props.documento ?? DOCUMENTO_PADRAO);
  const documento = props.documento ?? docInterno;
  const mudarDocumento = useCallback((p: Partial<DocumentoForense>) => {
    const novo = { ...documento, ...p };
    if (!props.documento) setDocInterno(novo);
    props.onDocumentoChange?.(novo);
  }, [documento, props]);
  // Para quem age depois (o "Desfazer" de um aviso): o documento de AGORA, não o do clique.
  const mudarDocumentoRef = useRef(mudarDocumento);
  mudarDocumentoRef.current = mudarDocumento;

  const [timbradosInternos, setTimbradosInternos] = useState<Timbrado[]>(props.timbrados ?? []);
  const timbrados = props.timbrados ?? timbradosInternos;
  const timbrado = timbrados.find((t) => t.id === documento.timbrado) ?? null;

  const [dicInterno, setDicInterno] = useState<string[]>(props.dicionarioPessoal ?? []);
  const dicionario = props.dicionarioPessoal ?? dicInterno;
  // "Ignorar" vale para esta sessão; "Adicionar ao dicionário" fica guardado.
  const ignoradas = useRef<string[]>([]);
  const dicRef = useRef(dicionario);
  dicRef.current = dicionario;
  const mudarDicionario = (lista: string[]) => { if (!props.dicionarioPessoal) setDicInterno(lista); props.onDicionarioPessoalChange?.(lista); };

  /* ---------- Versões ---------- */
  const [versoesInternas, setVersoesInternas] = useState<VersaoDoTexto[]>(SEM_VERSOES);
  const versoes = props.versoes ?? versoesInternas;
  const versoesRef = useRef(versoes);
  versoesRef.current = versoes;
  const ultimaVersaoRef = useRef<VersaoDoTexto | null>(null);
  ultimaVersaoRef.current = versoes.reduce<VersaoDoTexto | null>((u, v) => (!u || v.criadaEm > u.criadaEm ? v : u), null);
  // Versão = HTML de saída. A mesma de antes não se repete (salvar duas vezes sem mexer).
  const guardarVersaoRef = useRef((_o: OrigemVersao, _r?: string, _h?: string) => {});
  guardarVersaoRef.current = (origem, rotulo, htmlBruto) => {
    const bruto = htmlBruto ?? editorRef.current?.getHTML() ?? '';
    const html = bruto === '<p></p>' ? '' : htmlDeSaida(bruto);
    if (!html) return;
    if (!rotulo && ultimaVersaoRef.current?.html === html) return;
    const v: VersaoDoTexto = { id: `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, criadaEm: new Date().toISOString(), html, origem, ...(rotulo ? { rotulo } : {}) };
    ultimaVersaoRef.current = v;
    if (!props.versoes) setVersoesInternas((l) => [...l, v]);
    void Promise.resolve(onVersao?.(v)).catch(() => toast.error('Não consegui guardar a versão.'));
  };
  // "Como estava ao abrir": guardada no primeiro toque, se ainda não houver versão igual.
  const htmlAoAbrirRef = useRef<string | null>(null);
  const [comparando, setComparando] = useState<null | { id: string; contra: 'atual' | 'anterior' }>(null);
  // Textos das versões que a lista trouxe sem o `html`, buscados ao abrir uma.
  const [htmlsCarregados, setHtmlsCarregados] = useState<Record<string, string>>({});
  const carregarVersaoRef = useRef(props.carregarVersao);
  carregarVersaoRef.current = props.carregarVersao;
  useEffect(() => {
    if (!comparando) return;
    const ordem = [...versoesRef.current].sort((a, b) => a.criadaEm.localeCompare(b.criadaEm));
    const i = ordem.findIndex((v) => v.id === comparando.id);
    const precisa = [ordem[i], comparando.contra === 'anterior' ? ordem[i - 1] : undefined].filter(
      (v): v is VersaoDoTexto => !!v && v.html === undefined,
    );
    const carregar = carregarVersaoRef.current;
    if (!precisa.length || !carregar) return;
    let vivo = true;
    Promise.all(precisa.map(async (v) => [v.id, await carregar(v.id)] as const))
      .then((pares) => { if (vivo) setHtmlsCarregados((m) => ({ ...m, ...Object.fromEntries(pares) })); })
      .catch(() => {
        if (!vivo) return;
        toast.error('Não consegui abrir esta versão. Tente de novo.');
        setComparando(null);
      });
    return () => { vivo = false; };
  }, [comparando]);

  /* ---------- A IA e o que a pessoa mudou depois dela ---------- */
  const baseIARef = useRef<ChangeSet<Autoria> | null>(null);
  const ultimaEscritaIARef = useRef<unknown>(null);
  const houveRef = useRef(false);
  const avisoIARef = useRef<number | undefined>(undefined);
  const onAlteracaoRef = useRef(onAlteracaoDoUsuario);
  onAlteracaoRef.current = onAlteracaoDoUsuario;
  const avisarAlteracao = useCallback((imediato: boolean) => {
    window.clearTimeout(avisoIARef.current);
    const conferir = () => {
      const ed = editorRef.current;
      // "Houve" só com texto mudado: negrito ou troca de estilo não pedem à IA que releia.
      const h = !!ed && houveAlteracaoDoUsuario(baseIARef.current) && alteracoesDaPessoa(baseIARef.current, ed.state.doc).houve;
      if (h !== houveRef.current) { houveRef.current = h; onAlteracaoRef.current?.(h); }
    };
    if (imediato) conferir(); else avisoIARef.current = window.setTimeout(conferir, 1500);
  }, []);
  useEffect(() => () => window.clearTimeout(avisoIARef.current), []);

  /* ---------- Tela ---------- */
  // A largura do PRÓPRIO editor, não a da janela: dentro de um painel de
  // 600px numa tela larga, ele precisa da barra do celular. Ref de callback
  // porque o elemento troca (o provisório do carregamento e o definitivo) e
  // um observador preso ao primeiro passaria a medir zero.
  const [celular, setCelular] = useState(false);
  // Para os ouvintes de evento registrados uma vez (o clique no texto).
  const celularRef = useRef(false);
  celularRef.current = celular;
  const observador = useRef<ResizeObserver | null>(null);
  const raiz = useCallback((el: HTMLDivElement | null) => {
    observador.current?.disconnect();
    if (!el || typeof ResizeObserver === 'undefined') return;
    observador.current = new ResizeObserver(([e]) => { if (e.contentRect.width > 0) setCelular(e.contentRect.width < larguraCelular); });
    observador.current.observe(el);
  }, [larguraCelular]);

  // Tela cheia: o editor cobre a janela (no celular, a área de texto ficava
  // pequena entre o cabeçalho do app e a barra). CSS, não a Fullscreen API — o
  // Safari do iPhone não a tem para elementos; a página por trás não rola.
  const [telaCheiaPropria, setTelaCheia] = useState(false);
  const alternarTelaCheiaPropria = useCallback(() => setTelaCheia((t) => !t), []);
  const telaCheia = telaCheiaDoApp ? telaCheiaDoApp.ativa : telaCheiaPropria;
  const alternarTelaCheia = telaCheiaDoApp ? telaCheiaDoApp.alternar : alternarTelaCheiaPropria;
  useEffect(() => {
    if (!telaCheiaPropria) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Esc sai da tela cheia — a não ser que feche antes algo aberto por cima dela
    // (gaveta, painel do celular, menu, diálogo), que ainda está no DOM agora.
    const esc = (e: KeyboardEvent) => {
      // O núcleo do TipTap consome o Esc no texto; o de um campo (medida, nome) não sai.
      if (e.key !== 'Escape' || (e.defaultPrevented && !(e.target as HTMLElement | null)?.closest?.('.ProseMirror'))) return;
      if (document.querySelector('.cm-ef-gaveta-veu, .cm-ef-lateral-cel, [data-radix-popper-content-wrapper], [role="dialog"][data-state="open"]')) return;
      setTelaCheia(false);
    };
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = antes;
      document.removeEventListener('keydown', esc);
    };
  }, [telaCheiaPropria]);

  const [aba, setAba] = useState<Aba>(() => (props.abaDoAppInicial ? `app:${props.abaDoAppInicial}` : 'inicio'));
  const folhaRef = useRef<HTMLDivElement>(null);
  const [gerandoArquivo, setGerandoArquivo] = useState<FormatoArquivo | null>(null);
  const [recolhida, setRecolhida] = useState(() => (props.abaDoAppInicial ? false : barrasRecolhidas));
  // A aba do app que deixou de existir (o app parou de passá-la) volta ao Início:
  // senão a faixa ficava aberta e vazia.
  const abaEmUso: Aba = aba.startsWith('app:') && !props.abasDoApp?.some((a) => `app:${a.chave}` === aba) ? 'inicio' : aba;
  const onAbaDoAppRef = useRef(props.onAbaDoApp);
  useLayoutEffect(() => { onAbaDoAppRef.current = props.onAbaDoApp; });
  useEffect(() => {
    onAbaDoAppRef.current?.(abaEmUso.startsWith('app:') && !recolhida ? abaEmUso.slice(4) : null);
  }, [abaEmUso, recolhida]);
  // Quando o PRÓPRIO editor leva a uma aba (ortografia, Parágrafo…), a barra
  // abre: levar a uma aba fechada não mostraria nada.
  const abrirAba = useCallback((a: Aba) => {
    setAba(a);
    setRecolhida(false);
  }, []);
  // O clique na aba: abre a barra dela; na aba já aberta, recolhe (se a tela
  // quer barras recolhidas — sem isso, clicar na aba aberta não faz nada).
  const clicarAba = useCallback(
    (a: Aba) => {
      if (barrasRecolhidas && a === aba && !recolhida) setRecolhida(true);
      else abrirAba(a);
    },
    [aba, recolhida, barrasRecolhidas, abrirAba],
  );
  const [gaveta, setGaveta] = useState<Gaveta>(null);
  const [lateral, setLateral] = useState<null | 'busca' | 'versoes'>(null);
  const [focarSubstituir, setFocarSubstituir] = useState(false);
  const [paragrafoAberto, setParagrafoAberto] = useState(false);
  const [margensAberto, setMargensAberto] = useState(false);
  const [timbradoAberto, setTimbradoAberto] = useState(false);
  const [formatacaoAberta, setFormatacaoAberta] = useState(false);
  const [editandoCabecalho, setEditandoCabecalho] = useState(false);
  const [pincel, setPincel] = useState<{ estilo: string; ajustes: Partial<FormatoParagrafo> | null } | null>(null);
  const [corretorNativo, setCorretorNativo] = useState(true);
  const [orto, setOrto] = useState<null | { palavra: string; x: number; y: number; from: number; to: number }>(null);
  const [cicloCaixa, setCicloCaixa] = useState(0);

  const acaoRef = useRef<(a: AcaoDeTela) => void>(() => {});
  const provedorRef = useRef<ProvedorOrtografia | null>(ortografia);
  provedorRef.current = ortografia;

  /* ---------- Editor ---------- */
  const extensoes = useMemo(() => [
    StarterKit.configure({ orderedList: false, link: { openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } } }),
    NumeracaoJuridica,
    TextStyle, Color, BackgroundColor, FontFamily, FontSize,
    Highlight.configure({ multicolor: true }),
    Subscript, Superscript,
    Table.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          bordas: {
            default: 'todas',
            parseHTML: (el: HTMLElement) => el.getAttribute('data-bordas') || 'todas',
            renderHTML: (a: { bordas: string }) => ({ 'data-bordas': a.bordas }),
          },
          sombrear: {
            default: true,
            parseHTML: (el: HTMLElement) => el.getAttribute('data-sombrear') !== 'nao',
            renderHTML: (a: { sombrear: boolean }) => (a.sombrear ? {} : { 'data-sombrear': 'nao' }),
          },
          repetirCabecalho: {
            default: true,
            parseHTML: (el: HTMLElement) => el.getAttribute('data-repetir') !== 'nao',
            renderHTML: (a: { repetirCabecalho: boolean }) => (a.repetirCabecalho ? {} : { 'data-repetir': 'nao' }),
          },
        };
      },
    }).configure({ resizable: false }),
    TableRow, TableHeader, CelulaComFormula,
    Placeholder.configure({ placeholder }),
    CharacterCount,
    FormatoParagrafoExt,
    NotaRodape,
    DestaqueIA,
    Quebra.extend({ addNodeView() { return ReactNodeViewRenderer(QuebraView); } }),
    ColagemSegura,
    Busca,
    Ortografia.configure({ provedor: () => provedorRef.current, dicionarioPessoal: () => [...dicRef.current, ...ignoradas.current] }),
    SugestoesIA,
    AtalhosWord.configure({ aoAcionar: (a) => acaoRef.current(a) }),
  ], [placeholder]);

  const editor = useEditor({
    extensions: extensoes,
    content: valor,
    editable: !somenteLeitura,
    immediatelyRender: false,
    editorProps: { attributes: { class: 'cm-ef-texto', spellcheck: 'true', lang: 'pt-BR' } },
    onUpdate: ({ editor: e, transaction }) => {
      // O TipTap avisa "atualizado" ao montar, numa transação SEM passo. Chamar o
      // onChange aí faz o app com salvamento automático gravar o HTML normalizado
      // só por abrir (no Título, apagava o PDF conferido). Sem passo, nada mudou.
      if (!transaction.docChanged) return;
      const h = e.getHTML();
      const emitido = h === '<p></p>' ? '' : htmlDeSaida(h);
      confirmadoRef.current = emitido;
      onChange(emitido);
      // Recálculo de fórmula e renumeração de nota vão fora do histórico: não
      // são edição de quem escreve, e não sujam o texto (abria "não salvas").
      if (transaction.getMeta('addToHistory') !== false) {
        if (htmlAoAbrirRef.current !== null) {
          // Primeiro toque desde que o texto chegou: o original vira versão.
          const original = htmlAoAbrirRef.current;
          htmlAoAbrirRef.current = null;
          if (original && original !== '<p></p>' && !versoesRef.current.some((v) => v.html === htmlDeSaida(original))) guardarVersaoRef.current('abertura', undefined, original);
        }
        marcarSujoRef.current();
      }
    },
    onCreate: ({ editor: e }) => { htmlAoAbrirRef.current = e.getHTML(); },
    onTransaction: ({ transaction }) => {
      const base = baseIARef.current;
      if (!base || !transaction.docChanged) return;
      // Recálculo de fórmula e renumeração (fora do histórico) não são de ninguém: entram como da IA, que não pesa.
      const daIA = !!transaction.getMeta(ORIGEM_IA) || transaction.getMeta('addToHistory') === false;
      baseIARef.current = base.addSteps(transaction.doc, transaction.mapping.maps, daIA ? 'ia' : 'usuario');
      if (!daIA || houveRef.current) avisarAlteracao(false);
    },
  });

  editorRef.current = editor;

  /* ---------- Estilos do escritório: registro e redesenho ---------- */
  // Definido no render (antes de qualquer parágrafo se desenhar) e redesenhado
  // quando muda: o formato em linha de cada parágrafo é calculado do registro.
  // Os da peça (o "só para mim" de quem a escreveu) com os do escritório por cima:
  // é o que os parágrafos usam — iguais para quem abrir a peça, e no PDF.
  const ajustesDaPeca = documento.ajustesProntos ?? SEM_AJUSTES;
  const ajustesQueValemAqui = useMemo(
    () => ajustesQueValem(ajustesDosEstilosProntos, ajustesDaPeca, escritorioPrevalece),
    [ajustesDosEstilosProntos, ajustesDaPeca, escritorioPrevalece],
  );
  definirRegistroDeEstilos({ personalizados: estilosPersonalizados, ajustesProntos: ajustesQueValemAqui });
  const assinaturaEstilos = useMemo(
    () => JSON.stringify([estilosPersonalizados, ajustesQueValemAqui]),
    [estilosPersonalizados, ajustesQueValemAqui],
  );
  const primeiraAssinatura = useRef(assinaturaEstilos);
  useEffect(() => {
    if (!editor || assinaturaEstilos === primeiraAssinatura.current) return;
    primeiraAssinatura.current = assinaturaEstilos;
    editor.commands.redesenharEstilos();
  }, [editor, assinaturaEstilos]);
  const [criandoEstilo, setCriandoEstilo] = useState(false);
  const [gerenciandoEstilos, setGerenciandoEstilos] = useState(false);


  // O provedor pode chegar depois (carregado sob demanda): a extensão o lê
  // pela ref a cada verificação; aqui só se pede uma verificação nova.
  useEffect(() => { if (editor && !editor.isDestroyed) editor.commands.reverificar(); }, [editor, ortografia]);
  useEffect(() => { if (editor && !editor.isDestroyed) editor.commands.reverificar(); }, [editor, dicionario]);

  // Conteúdo trocado por fora (outra peça aberta, descarte). Destruído, o TipTap 3 anula
  // o `schema` e o `getHTML()` cai ("reading 'cached'"); a instância nova chega no próximo
  // render — o mesmo defeito que derrubou a tela de peticionar.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const atual = htmlDeSaida(editor.getHTML());
    if (valor !== atual && !(valor === '' && atual === '<p></p>')) {
      // Troca por fora (outra peça, o rascunho do servidor) não é a pessoa mexendo.
      editor.chain().setMeta(ORIGEM_IA, true).setContent(valor || '', { emitUpdate: false }).run();
      // Outra peça (ou o rascunho do servidor): é ela o "como estava ao abrir".
      htmlAoAbrirRef.current = editor.getHTML();
    }
    if (confirmadoRef.current !== valor) {
      const h = editor.getHTML();
      const saida = h === '<p></p>' ? '' : htmlDeSaida(h);
      confirmadoRef.current = saida;
      onValorNormalizadoRef.current?.(saida);
    }
  }, [editor, valor]);

  useEffect(() => { editor?.setEditable(!somenteLeitura); }, [editor, somenteLeitura]);
  useEffect(() => { editor?.view.dom.setAttribute('spellcheck', corretorNativo ? 'true' : 'false'); }, [editor, corretorNativo]);

  useImperativeHandle(ref, () => ({
    appendText: (t) => { editor?.chain().focus().insertContent(t).run(); },
    focus: () => { editor?.commands.focus(); },
    editor,
    marcarVersaoVistaPelaIA: () => {
      if (!editor) return;
      baseIARef.current = novaBaseDaIA(editor.state.doc);
      avisarAlteracao(true);
    },
    alteracoesDesdeIA: () => {
      const doc = editor?.state.doc;
      return doc ? alteracoesDaPessoa(baseIARef.current, doc) : { houve: false, trechos: [], resumo: '', texto: '' };
    },
    segurarSalvamento: (segurar) => {
      seguradoRef.current = segurar;
      // Solto, a espera máxima conta de agora: a minuta inteira é uma alteração só.
      if (!segurar) primeiraAlteracaoRef.current = null;
      agendarRef.current();
    },
    escreverPelaIA: (bruto, onde = 'selecao') => {
      if (!editor) return;
      const html = htmlDaIANosEstilos(bruto);
      // A IA escrevendo em pedaços (streaming): versão só antes do primeiro, e
      // de novo se a pessoa mexer entre uma escrita e outra.
      if (onde === 'tudo' && editor.state.doc !== ultimaEscritaIARef.current) guardarVersaoRef.current('ia');
      // A escrita acompanha o fim do texto enquanto a pessoa estiver nele; quem
      // rolou para cima, para ler, fica onde está.
      const m = mesaRef.current;
      const seguir = onde !== 'selecao' && (!m || m.scrollTop + m.clientHeight >= m.scrollHeight - 120);
      const c = editor.chain().setMeta(ORIGEM_IA, true);
      if (onde === 'tudo') c.setContent(html, { emitUpdate: true });
      else if (onde === 'fim') c.insertContentAt(editor.state.doc.content.size, html);
      else c.insertContent(html);
      if (seguir) c.command(({ tr }) => { tr.setSelection(Selection.atEnd(tr.doc)).scrollIntoView(); return true; });
      c.run();
      ultimaEscritaIARef.current = editor.state.doc;
      // Sem nada da pessoa pendente, a IA "leu" o que acabou de escrever.
      if (baseIARef.current && !houveRef.current) baseIARef.current = novaBaseDaIA(editor.state.doc);
    },
    ajustarPelaIA: (bruto, base) => {
      if (!editor) return { alterados: 0, removidos: 0, mantidos: 0 };
      const versaoAntes = editor.getHTML();
      const tr = editor.state.tr.setMeta(ORIGEM_IA, true);
      const resultado = aplicarAjusteDaIA(editor, bruto, tr, (base as PMNode | null | undefined) ?? null);
      if (resultado.recusa) return resultado;
      guardarVersaoRef.current('ia', undefined, versaoAntes);
      editor.view.dispatch(tr);
      // O primeiro parágrafo mudado fora da vista: a mesa rola até ele (só a mesa,
      // não a página), deixando-o a um terço da altura.
      const primeiro = CHAVE_DESTAQUE_IA.getState(editor.state)?.find().reduce((a, d) => Math.min(a, d.from), Infinity);
      const m = mesaRef.current;
      const el = primeiro !== undefined && Number.isFinite(primeiro) ? editor.view.nodeDOM(primeiro) : null;
      if (m && el instanceof HTMLElement) {
        const caixa = el.getBoundingClientRect();
        const vista = m.getBoundingClientRect();
        // O aviso do resultado cobre a faixa de baixo da mesa: ali não conta como visível.
        if (caixa.top < vista.top || caixa.bottom > vista.bottom - 80) {
          m.scrollTo({ top: m.scrollTop + caixa.top - vista.top - m.clientHeight / 3, behavior: 'smooth' });
        }
      }
      ultimaEscritaIARef.current = editor.state.doc;
      if (baseIARef.current && !houveRef.current) baseIARef.current = novaBaseDaIA(editor.state.doc);
      return resultado;
    },
    textoParaIA: () => (editor ? htmlParaIA(editor.state.doc) : null),
    limparDestaquesDaIA: () => { if (editor) limparDestaqueDaIA(editor); },
    guardarVersao: (rotulo) => guardarVersaoRef.current(rotulo ? 'nomeada' : 'salvamento', rotulo),
    abrirAbaDoApp: (chave) => abrirAba(`app:${chave}`),
  }), [editor, avisarAlteracao, abrirAba]);

  /* ---------- Estado para as barras ---------- */
  const est: EstadoEditor | null = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      const f = formatoNoCursor(e);
      const ts = e.getAttributes('textStyle');
      const cel = celulaNoCursor(e.state);
      return {
        estilo: f.estilo, modificado: f.modificado,
        formato: { ...f.formato, ...(ts.fontFamily ? { fonte: (ts.fontFamily as string).split(',')[0].replace(/"/g, '').trim() } : {}), ...(ts.fontSize ? { tamanho: parseFloat(ts.fontSize as string) || f.formato.tamanho } : {}) },
        negrito: e.isActive('bold'), italico: e.isActive('italic'), sublinhado: e.isActive('underline'), tachado: e.isActive('strike'),
        sobrescrito: e.isActive('superscript'), subscrito: e.isActive('subscript'),
        numerado: e.isActive('orderedList'), marcadores: e.isActive('bulletList'),
        noTabela: !!cel, referencia: referenciaNoCursor(e.state),
        formulaCelula: cel ? (cel.node.attrs.formula as string | null) ?? cel.node.textContent : '',
        palavras: contarPalavras(e.state.doc.textBetween(0, e.state.doc.content.size, ' ', ' ')),
        podeDesfazer: e.can().undo(), podeRefazer: e.can().redo(),
        ortografiaAtiva: CHAVE_ORTO.getState(e.state)?.ativa ?? false,
        corretorNativo, pincel: !!pincel,
        corTexto: (ts.color as string) || '#1B1B1F', corRealce: (e.getAttributes('highlight').color as string) || '#F6E27A',
      };
    },
  });
  // Busca e sugestões vivem em plugins: sem assiná-los aqui, o painel lê o
  // estado do render anterior ("Nenhuma ocorrência" com dois achados na tela).
  // `caracteres` é o que "Revisar de novo" cobra: o texto de AGORA, aparado como o
  // hook o envia — não o da revisão anterior, senão a dica mostra um número e o
  // servidor debita outro. Só com a revisão por IA ligada (lê a peça a cada passo).
  const plugins = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      const bs = buscaNoEstado(e.state);
      return {
        achados: bs?.achados.length ?? 0, atual: bs?.atual ?? -1, sugestoes: sugestoesNoEstado(e.state).map((s) => `${s.id}:${s.resolvida ?? ''}`).join(','), foco: CHAVE_IA.getState(e.state)?.foco ?? null,
        caracteres: creditosIA ? e.getText().trim().length : 0,
      };
    },
  });

  /* ---------- Ações ---------- */
  const ajustar = useCallback((a: Partial<FormatoParagrafo>) => { editor?.chain().focus().ajustarParagrafo(a).run(); }, [editor]);
  const desfazer = () => { editor?.chain().focus().undo().run(); };

  const colar = useCallback(async (modoColar: 'formatado' | 'texto') => {
    if (!editor) return;
    try {
      if (modoColar === 'formatado' && navigator.clipboard && 'read' in navigator.clipboard) {
        const itens = await navigator.clipboard.read();
        for (const item of itens) {
          if (item.types.includes('text/html')) {
            const html = await (await item.getType('text/html')).text();
            editor.chain().focus().insertContent(limparHtmlColado(html)).run();
            toast.operation('Colado sem as fontes e margens de fora. Negrito e itálico mantidos.', desfazer);
            return;
          }
        }
      }
      const texto = await navigator.clipboard.readText();
      editor.chain().focus().insertContent(texto.replace(/\r?\n/g, ' ')).run();
      toast.operation(modoColar === 'texto' ? 'Colado só o texto, no estilo do parágrafo.' : 'Colado.', desfazer);
    } catch {
      toast.info(modoColar === 'texto' ? 'O navegador não liberou a área de transferência. Use Ctrl+Shift+V no texto.' : 'O navegador não liberou a área de transferência. Use Ctrl+V no texto.');
    }
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  const caixa = useCallback((modoCaixa: 'alta' | 'baixa' | 'titulo' | 'ciclo') => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (empty) { toast.info('Selecione o trecho primeiro.'); return; }
    const m = modoCaixa === 'ciclo' ? (['alta', 'baixa', 'titulo'] as const)[cicloCaixa % 3] : modoCaixa;
    if (modoCaixa === 'ciclo') setCicloCaixa((c) => c + 1);
    // Texto a texto, para manter as marcas (negrito no meio continua negrito).
    const tr = editor.state.tr;
    editor.state.doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isText || !node.text) return;
      const ini = Math.max(from, pos);
      const fim = Math.min(to, pos + node.text.length);
      const trecho = node.text.slice(ini - pos, fim - pos);
      const novo = m === 'alta' ? trecho.toLocaleUpperCase('pt-BR') : m === 'baixa' ? trecho.toLocaleLowerCase('pt-BR')
        : trecho.toLocaleLowerCase('pt-BR').replace(/(^|\s)(\S)/g, (_x, a: string, c: string) => a + c.toLocaleUpperCase('pt-BR'));
      tr.replaceWith(tr.mapping.map(ini), tr.mapping.map(fim), editor.schema.text(novo, node.marks));
    });
    editor.view.dispatch(tr);
  }, [editor, cicloCaixa]);

  const verificarOrtografia = useCallback(() => {
    if (!editor) return;
    abrirAba('revisar');
    const decs = CHAVE_ORTO.getState(editor.state)?.decoracoes.find() ?? [];
    if (!ortografia) { toast.info('O dicionário embutido não está ligado neste app; o corretor do navegador continua sublinhando.'); return; }
    if (!decs.length) { toast.success('Nenhum erro de ortografia encontrado.'); return; }
    const d = decs[0];
    editor.chain().focus().setTextSelection({ from: d.from, to: d.to }).scrollIntoView().run();
    const coords = editor.view.coordsAtPos(d.from);
    setOrto({ palavra: editor.state.doc.textBetween(d.from, d.to), x: coords.left, y: coords.bottom, from: d.from, to: d.to });
  }, [editor, ortografia, abrirAba]);

  const abrirLateral = useCallback((qual: 'busca' | 'versoes', subst = false) => {
    setLateral(qual);
    setFocarSubstituir(subst);
    setGaveta(null);
  }, []);

  acaoRef.current = (a) => {
    if (a === 'salvar') { salvarRef.current(); return; }
    if (a === 'localizar') abrirLateral('busca');
    else if (a === 'substituir') abrirLateral('busca', true);
    else if (a === 'ortografia') verificarOrtografia();
    else if (a === 'pincel') alternarPincel();
    else if (a === 'caixa') caixa('ciclo');
    else if (a === 'colarTexto') colar('texto');
    else if (a === 'link' && props.onRequestLink) void (async () => { const url = await props.onRequestLink!(); if (url) editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run(); })();
  };

  function alternarPincel() {
    if (!editor) return;
    if (pincel) { setPincel(null); return; }
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d >= 0; d--) {
      const n = $from.node(d);
      if (n.type.name === 'paragraph') { setPincel({ estilo: n.attrs.estilo, ajustes: n.attrs.ajustes }); toast.info('Formatação copiada. Clique no parágrafo que deve recebê-la.'); return; }
    }
  }
  // O próximo clique num parágrafo recebe a formatação do pincel. O alvo sai das
  // coordenadas do clique, não da seleção: um quadro depois do mouseup o
  // ProseMirror ainda não leu a seleção nova, e o pincel caía no parágrafo de
  // origem.
  useEffect(() => {
    if (!editor || !pincel) return;
    const dom = editor.view.dom;
    const aoSoltar = (e: MouseEvent) => {
      const alvo = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
      if (!alvo) return;
      const $p = editor.state.doc.resolve(alvo.pos);
      for (let d = $p.depth; d > 0; d--) {
        const n = $p.node(d);
        if (n.type.name !== 'paragraph') continue;
        editor.view.dispatch(editor.state.tr.setNodeMarkup($p.before(d), undefined, { ...n.attrs, estilo: pincel.estilo ?? 'corpo', ajustes: pincel.ajustes ?? null }));
        toast.operation('Formatação aplicada.', desfazer);
        break;
      }
      setPincel(null);
    };
    dom.addEventListener('mouseup', aoSoltar, { once: true });
    return () => dom.removeEventListener('mouseup', aoSoltar);
  }, [editor, pincel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clique numa palavra sublinhada pelo dicionário embutido: sugestões.
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const aoClicar = (e: MouseEvent) => {
      const alvo = (e.target as HTMLElement).closest('.cm-ef-erro') as HTMLElement | null;
      const sug = (e.target as HTMLElement).closest('[data-sugestao]') as HTMLElement | null;
      // O balão abre junto do trecho, sem tirar o cursor do texto. No celular o
      // cartão fica no pé: o trecho sobe para não ficar debaixo dele.
      if (sug) {
        focarBalaoRef.current = false;
        editor.commands.focarSugestao(sug.dataset.sugestao!);
        if (celularRef.current) sug.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        return;
      }
      // Clique fora de um trecho marcado fecha o cartão (no celular não há "clique fora" do balão).
      if (CHAVE_IA.getState(editor.state)?.foco) editor.commands.focarSugestao(null);
      if (!alvo) return;
      const pos = editor.view.posAtDOM(alvo, 0);
      const palavra = alvo.dataset.palavra ?? alvo.textContent ?? '';
      const r = alvo.getBoundingClientRect();
      setOrto({ palavra, x: r.left, y: r.bottom, from: pos, to: pos + palavra.length });
    };
    dom.addEventListener('click', aoClicar);
    return () => dom.removeEventListener('click', aoClicar);
  }, [editor]);

  /* ---------- IA ---------- */
  // A barra da revisão aparece no clique em "Revisar com IA" e fica até a pessoa fechá-la.
  const [revisaoAberta, setRevisaoAberta] = useState(false);
  const [iaEstado, setIaEstado] = useState<'pronto' | 'revisando' | 'erro'>('pronto');
  const [iaErro, setIaErro] = useState('');
  const [perdidas, setPerdidas] = useState(0);
  // Cada pedido tem uma geração; encerrar a revisão ou pedir outra a avança, e a
  // resposta de uma geração vencida é descartada (marcaria o texto sem barra).
  const geracaoIARef = useRef(0);
  const revisandoRef = useRef(false);
  const revisar = useCallback(async () => {
    // Um pedido de cada vez: cada um é cobrado (o botão do celular não se desativa).
    if (!editor || !revisarIA || revisandoRef.current) return;
    const geracao = ++geracaoIARef.current;
    revisandoRef.current = true;
    setRevisaoAberta(true);
    setIaEstado('revisando');
    editor.commands.focarSugestao(null);
    const texto = editor.getText();
    try {
      const lista = await revisarIA(texto);
      if (geracao !== geracaoIARef.current) return;
      editor.commands.definirSugestoes(lista);
      setIaEstado('pronto');
      // As que não acharam o trecho (o texto mudou enquanto a IA lia) não aparecem: a barra conta.
      setPerdidas(lista.length - (CHAVE_IA.getState(editor.state)?.lista.length ?? 0));
      // A primeira sugestão à vista: fora da tela, a pessoa só a achava rolando. Só
      // rola (sem abrir o balão nem tirar o cursor do lugar), e nada se já está à vista.
      const primeira = sugestoesNoEstado(editor.state).sort((a, b) => a.from - b.from)[0];
      if (primeira) {
        requestAnimationFrame(() => {
          if (editor.isDestroyed) return;
          editor.view.dom
            .querySelector(`[data-sugestao="${CSS.escape(primeira.id)}"]`)
            ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        });
      }
    } catch (e) {
      if (geracao !== geracaoIARef.current) return;
      setIaErro(e instanceof Error ? e.message : 'A revisão falhou.');
      setIaEstado('erro');
    } finally {
      if (geracao === geracaoIARef.current) revisandoRef.current = false;
    }
  }, [editor, revisarIA]);
  // Navegando pela barra ou pelo teclado, o foco vai ao Aceitar do balão (Enter aceita);
  // clicando no trecho, fica no texto.
  const focarBalaoRef = useRef(false);
  const cartaoRef = useRef<HTMLDivElement>(null);
  const irParaSugestao = useCallback((id: string | null) => {
    if (!editor) return;
    focarBalaoRef.current = !!id;
    editor.commands.focarSugestao(id);
    if (!id) return;
    editor.view.dom.querySelector(`[data-sugestao="${CSS.escape(id)}"]`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    // O balão monta (ou troca de cartão) no desenho seguinte; só então há Aceitar a focar.
    requestAnimationFrame(() => requestAnimationFrame(() => { if (focarBalaoRef.current) focarCartao(cartaoRef.current); }));
  }, [editor]);
  /** A pendente seguinte (ou anterior) à em foco, na ordem do texto, dando a volta. */
  const vizinhaPendente = useCallback((passo: 1 | -1, aPartirDe?: { id: string; from: number }) => {
    if (!editor) return null;
    const pend = sugestoesNoEstado(editor.state).filter((s) => !s.resolvida).sort((a, b) => a.from - b.from);
    if (!pend.length) return null;
    const ref = aPartirDe ?? pend.find((s) => s.id === CHAVE_IA.getState(editor.state)?.foco);
    if (!ref) return passo === 1 ? pend[0] : pend[pend.length - 1];
    const depois = pend.filter((s) => s.id !== ref.id && (passo === 1 ? s.from >= ref.from : s.from <= ref.from));
    const alvo = passo === 1 ? depois[0] : depois[depois.length - 1];
    return alvo ?? (passo === 1 ? pend[0] : pend[pend.length - 1]);
  }, [editor]);
  const resolverEAvancar = useCallback((id: string, aceitar: boolean) => {
    if (!editor || somenteLeitura) return;
    const s = sugestoesNoEstado(editor.state).find((x) => x.id === id);
    if (!s) return;
    const aplicou = editor.commands.resolverSugestao(id, aceitar);
    // Caducou ao aceitar: o balão fica nela, mostrando por quê, e o foco vai ao
    // fechar (o Aceitar sumiu; sem isto o foco caía no <body>).
    if (!aplicou) {
      requestAnimationFrame(() => focarCartao(cartaoRef.current));
      return;
    }
    const prox = vizinhaPendente(1, { id, from: s.from });
    if (prox) irParaSugestao(prox.id);
    // A última: o balão fecha e o cursor volta ao texto.
    else { focarBalaoRef.current = false; editor.chain().focus().focarSugestao(null).run(); }
  }, [editor, somenteLeitura, vizinhaPendente, irParaSugestao]);
  const fecharBalao = useCallback(() => {
    if (!editor) return;
    focarBalaoRef.current = false;
    editor.chain().focus().focarSugestao(null).run();
  }, [editor]);
  const encerrarRevisao = useCallback(() => {
    if (!editor) return;
    // A resposta ainda a caminho não marca mais o texto (já foi cobrada; não há como cancelar).
    geracaoIARef.current++;
    revisandoRef.current = false;
    editor.commands.definirSugestoes([]);
    setRevisaoAberta(false);
    setIaEstado('pronto');
    setPerdidas(0);
  }, [editor]);

  /* ---------- Busca ---------- */
  const [busca, setBusca] = useState({ termo: '', novo: '', caixa: false, inteira: true });
  useEffect(() => {
    if (!editor) return;
    if (lateral === 'busca') editor.commands.buscar(busca.termo, { diferenciarCaixa: busca.caixa, palavraInteira: busca.inteira });
    else editor.commands.limparBusca();
  }, [editor, lateral, busca.termo, busca.caixa, busca.inteira]);

  /* ---------- Contexto da folha ---------- */
  const ctxFolha: ContextoFolha = {
    editor, documento, timbrado, editandoCabecalho,
    editarNota: (pos, texto) => editor?.chain().definirTextoNota(pos, texto).run(),
    definirCabecalhoRodape: (qual, html) => mudarDocumento(qual === 'cabecalho' ? { cabecalho: html } : { rodape: html }),
  };

  if (!editor || !est) return <div ref={raiz} className={`cm-ef${className ? ` ${className}` : ''}`} style={style} />;
  const estado: EstadoEditor = { ...est, pincel: !!pincel, corretorNativo };

  /**
   * A formatação medida de um arquivo: na peça, os parágrafos no Corpo da peça
   * ganham o formato como ajuste; como modelo, o formato vira o ajuste do Corpo
   * da peça do escritório. Margens e papel, nos dois casos, só nesta peça.
   */
  /** Guarda um timbrado (o app grava no servidor; sem o app, fica nesta sessão). */
  function guardarTimbrado(t: Timbrado): Promise<void> {
    if (!props.timbrados) setTimbradosInternos((l) => [...l.filter((x) => x.id !== t.id), t]);
    return Promise.resolve(props.onTimbradoSalvo?.(t)).catch(() => {});
  }

  /**
   * O que fazer com o cabeçalho e o rodapé de um arquivo: virar o timbrado da
   * pessoa; não, quando o do escritório vale por cima (somente leitura); nada,
   * quando o app tem timbrados mas não grava (a conta de outra pessoa).
   */
  const destinoDoTimbrado: 'proprio' | 'escritorio' | null = timbrados.some((t) => t.somenteLeitura)
    ? 'escritorio'
    : props.onTimbradoSalvo || !props.timbrados ? 'proprio' : null;

  /**
   * Guarda a página das peças novas e devolve o "desfazer" (volta a de antes).
   * A gravação recusada rejeita (o app já avisou).
   */
  function guardarPaginaPadrao(p: PaginaPadrao | null): Promise<() => void> {
    const anterior = props.paginaPadrao ?? null;
    return Promise.resolve(props.onPaginaPadrao?.(p)).then(() => () => { void Promise.resolve(props.onPaginaPadrao?.(anterior)).catch(() => {}); });
  }

  function acaoPaginaPadrao(p: PaginaPadrao | null, aviso: string) {
    guardarPaginaPadrao(p).then((voltar) => toast.operation(aviso, voltar, { duration: 6000 }), () => {});
  }

  function aplicarFormatacao(x: ExtracaoDeArquivo, { comoModelo, usarTimbrado }: { comoModelo: AlcanceDoModelo | null; usarTimbrado: boolean }) {
    const ed = editor!;
    setFormatacaoAberta(false);
    const temFormato = Object.keys(x.formato).length > 0;
    // O ajuste guardado no lugar escolhido (o seu ou o do escritório): a base do novo e o que o "Desfazer" devolve.
    const ajustesDoAlcance = comoModelo === 'pessoal' ? ajustesPessoaisDosEstilosProntos : ajustesDosEstilosProntos;
    const ajusteAnterior = ajustesDoAlcance[ESTILO_PADRAO] ?? null;
    let mudouModelo = false;
    let mudouTexto = false;
    let ajustesPessoaisNovos: Partial<FormatoParagrafo> = {};
    if (temFormato && comoModelo && onAjustarEstiloPronto) {
      const base = formatoDoPronto(ESTILO_PADRAO, false);
      const ajustes = diferencaDeFormato(base, { ...base, ...(ajusteAnterior ?? {}), ...x.formato });
      void Promise.resolve(onAjustarEstiloPronto(ESTILO_PADRAO, Object.keys(ajustes).length ? ajustes : null, comoModelo));
      ajustesPessoaisNovos = ajustes;
      mudouModelo = true;
    } else if (temFormato) {
      mudouTexto = ed.chain().focus().ajustarEstiloNoTexto(ESTILO_PADRAO, x.formato).run();
    }
    // O "Desfazer" do texto só desfaz se o último passo ainda for este (a pessoa
    // pode ter digitado depois); o da página volta só o que a formatação mudou.
    const profundidade = undoDepth(ed.state);
    const { margens, papel, orientacao } = x.documento;
    const pagina: Partial<DocumentoForense> = {};
    if (margens && Object.keys(margens).length) Object.assign(pagina, { margens: { ...documento.margens, ...margens }, nomeMargens: 'personalizadas' });
    if (papel) pagina.papel = papel;
    if (orientacao) pagina.orientacao = orientacao;
    if (x.timbrado?.numeroDePagina) pagina.paginas = { ...documento.paginas, ativa: true };
    // O seu Corpo da peça vale já nesta peça (gravado nela, como nas próximas).
    // Por cima do Corpo que a peça já guarda (o de quem a escreveu): só o medido muda.
    if (mudouModelo && comoModelo === 'pessoal') {
      pagina.ajustesProntos = { ...ajustesDaPeca, [ESTILO_PADRAO]: { ...(ajustesDaPeca[ESTILO_PADRAO] ?? {}), ...ajustesPessoaisNovos } };
    }
    // O timbrado guardado é um só por pessoa: o do arquivo toma o lugar dele
    // (texto e desenho; sem desenho no arquivo, o logotipo fica) e passa a
    // valer nesta peça.
    const timbradoAnterior = timbrados.find((t) => !t.somenteLeitura) ?? null;
    const timbradoNaPeca = documento.timbrado;
    const timbradoNovo = usarTimbrado && destinoDoTimbrado === 'proprio' ? timbradoDoArquivo(x, timbradoAnterior, 'arquivo') : null;
    let salvando: Promise<void> = Promise.resolve();
    if (timbradoNovo) {
      salvando = guardarTimbrado(timbradoNovo);
      // Sem timbrado guardado, o id é o do app, que o liga na peça ao gravar;
      // sem o app (o editor sozinho), é o desta sessão.
      if (timbradoAnterior || !props.timbrados) pagina.timbrado = timbradoNovo.id;
    }
    const mudouPagina = (Object.keys(pagina) as (keyof DocumentoForense)[]).some((k) => JSON.stringify(pagina[k]) !== JSON.stringify(documento[k]));
    // Como modelo, a folha medida (papel, orientação, margens) vira a das próximas peças.
    const paginaNova = paginaDoDocumento({ ...documento, ...pagina });
    const mudouPaginaPadrao = comoModelo && !!props.onPaginaPadrao && !!(margens || papel || orientacao)
      && !mesmaPagina(paginaNova, props.paginaPadrao ?? paginaDoDocumento(DOCUMENTO_PADRAO));
    const guardandoPagina = mudouPaginaPadrao ? guardarPaginaPadrao(paginaNova).catch(() => null) : null;
    const paginaAnterior = Object.fromEntries(Object.keys(pagina).map((k) => [k, documento[k as keyof DocumentoForense]])) as Partial<DocumentoForense>;
    if (mudouPagina) mudarDocumento(pagina);

    if (!mudouModelo && !mudouTexto && !mudouPagina && !timbradoNovo && !mudouPaginaPadrao) {
      toast.info(temFormato
        ? 'Nada mudou: a peça já tem esta formatação, ou nenhum parágrafo dela está no Corpo da peça.'
        : 'Nada mudou nesta peça.');
      return;
    }
    // A página das próximas peças só entra no aviso depois de gravada (a recusa, o app já avisou).
    const corpo = comoModelo === 'pessoal' ? 'o seu Corpo da peça' : 'o Corpo da peça do escritório';
    const avisar = (paginaGuardada: boolean) => toast.operation(
      mudouModelo && paginaGuardada ? `Formatação aplicada: ${corpo} e a página das próximas peças mudaram.`
        : mudouModelo ? `Formatação aplicada: ${corpo} mudou.`
          : paginaGuardada ? 'Formatação aplicada: a página das próximas peças mudou.'
            : 'Formatação aplicada a esta peça.',
      () => {
        if (mudouTexto && undoDepth(ed.state) === profundidade) desfazer();
        if (mudouModelo) void Promise.resolve(onAjustarEstiloPronto?.(ESTILO_PADRAO, ajusteAnterior, comoModelo ?? undefined));
        if (mudouPagina) mudarDocumentoRef.current(paginaAnterior);
        void guardandoPagina?.then((voltar) => voltar?.());
        if (!timbradoNovo) return;
        // Sem timbrado antes, volta a nenhum: sem texto, sem faixa e sem logotipo.
        const anterior = timbradoAnterior ?? { id: timbradoNovo.id, rotulo: timbradoNovo.rotulo, modo: 'texto' as const, cabecalhoTexto: '', rodapeTexto: '' };
        // Em ordem: gravar liga o timbrado na peça (o app), então a peça volta
        // ao que era só depois das duas gravações.
        void salvando
          .then(() => guardarTimbrado(anterior))
          .then(() => mudarDocumentoRef.current({ timbrado: timbradoNaPeca }));
      },
      { duration: 6000 },
    );
    if (guardandoPagina) {
      void guardandoPagina.then((voltar) => { if (voltar || mudouModelo || mudouTexto || mudouPagina || timbradoNovo) avisar(!!voltar); });
    }
    else avisar(false);
  }

  function acoesArquivo(): AcoesEditor['arquivo'] {
    const ed = editor!;
    const titulo = props.nomeArquivo?.trim() || 'Peça';
    const gerar = props.onGerarArquivo;
    const formatos: FormatoArquivo[] = gerar ? ['docx', 'pdf', 'html'] : ['html'];
    const html = () => { const h = ed.getHTML(); return h === '<p></p>' ? '' : htmlDeSaida(h); };
    const imprimir = () => { if (folhaRef.current) void imprimirFolha(folhaRef.current, documento, titulo); };
    const vazio = () => !textoPuro(html()).trim();
    const arquivo = async (formato: FormatoArquivo): Promise<{ blob: Blob; nome: string } | null> => {
      if (formato === 'html') return { blob: paginaHtml(html(), documento, titulo), nome: nomeDeArquivo(titulo, 'html') };
      setGerandoArquivo(formato);
      try {
        const blob = await gerar?.(formato);
        return blob ? { blob, nome: nomeDeArquivo(titulo, formato) } : null;
      } finally {
        setGerandoArquivo(null);
      }
    };
    const compartilhaArquivo = podeCompartilharArquivo();
    const copiar = (texto: string) => navigator.clipboard?.writeText(texto) ?? Promise.reject(new Error('sem área de transferência'));
    return {
      formatos,
      gerando: gerandoArquivo,
      compartilhaArquivo,
      imprimir,
      baixar: async (formato) => {
        if (vazio()) { toast.info('A peça está vazia: escreva algo antes de baixar.'); return; }
        try {
          const a = await arquivo(formato);
          if (!a) return;
          baixarBlob(a.blob, a.nome);
          toast.success(`Baixado: ${a.nome}`);
        } catch {
          toast.error('Não consegui gerar o arquivo. Tente de novo ou escolha outro formato.');
        }
      },
      compartilhar: async (formato) => {
        if (vazio()) { toast.info('A peça está vazia: escreva algo antes de enviar.'); return; }
        try {
          const a = await arquivo(formato);
          if (!a) return;
          // Onde o navegador não compartilha arquivo, baixa: anexar é com a pessoa.
          // Desistir no menu do sistema não é erro: nada a dizer.
          const resultado = compartilhaArquivo ? await compartilharArquivo(a.blob, a.nome, titulo) : 'recusado';
          if (resultado === 'recusado') {
            baixarBlob(a.blob, a.nome);
            toast.success(`Baixado: ${a.nome}. Anexe-o na mensagem.`);
          }
        } catch {
          toast.error('Não consegui preparar o arquivo para enviar.');
        }
      },
      enviarPorEmail: async () => {
        const texto = textoPuro(html());
        const { href, cabe } = linkDeEmail(titulo, texto);
        if (!cabe) {
          try { await copiar(texto); toast.info('A peça é longa para o e-mail: o texto foi copiado. Cole no corpo da mensagem.'); }
          catch { toast.info('A peça é longa para o e-mail: baixe em Word ou PDF e anexe.'); return; }
        }
        // Por iframe, não pela janela: navegar para mailto: dispararia o aviso de
        // "alterações não salvas" do beforeunload.
        const f = document.createElement('iframe');
        f.style.display = 'none';
        f.src = href;
        document.body.appendChild(f);
        window.setTimeout(() => f.remove(), 2000);
      },
      copiarTexto: () => {
        copiar(textoPuro(html())).then(() => toast.operation('Texto da peça copiado.'), () => toast.info('Não consegui copiar. Selecione tudo (Ctrl+A) e use Ctrl+C.'));
      },
      detalhes: () => ({ ...detalhesDoTexto(textoPuro(html())), paginas: totalDePaginas(ed.state.doc) }),
    };
  }

  const acoes: AcoesEditor = {
    ajustar,
    aplicarFonte: (nome) => (editor.state.selection.empty ? ajustar({ fonte: nome }) : editor.chain().focus().setFontFamily(pilhaDaFonte(nome)).run()),
    aplicarTamanho: (pt) => (editor.state.selection.empty ? ajustar({ tamanho: pt }) : editor.chain().focus().setFontSize(`${pt}pt`).run()),
    copiar: () => { editor.commands.focus(); if (!document.execCommand('copy')) toast.info('Use Ctrl+C.'); else toast.operation('Copiado.'); },
    recortar: () => { editor.commands.focus(); if (!document.execCommand('cut')) toast.info('Use Ctrl+X.'); else toast.operation('Recortado.', desfazer); },
    colar, caixa: (m) => caixa(m), alternarPincel,
    // No computador, a aba Parágrafo (opções à vista); no celular, sem abas, o painel.
    abrirParagrafo: () => (celular ? setParagrafoAberto(true) : abrirAba('paragrafo')),
    abrirMargens: () => setMargensAberto(true),
    paginaPadrao: props.onPaginaPadrao && !somenteLeitura ? {
      ehEsta: mesmaPagina(paginaDoDocumento(documento), props.paginaPadrao ?? paginaDoDocumento(DOCUMENTO_PADRAO)),
      guardada: !!props.paginaPadrao,
      usar: () => acaoPaginaPadrao(paginaDoDocumento(documento), 'As próximas peças abrem com esta página.'),
      voltar: () => acaoPaginaPadrao(null, 'As próximas peças abrem em A4, com as margens ABNT.'),
    } : null,
    abrirTimbrado: () => setTimbradoAberto(true),
    abrirFormatacao: extrairFormatacao && !somenteLeitura ? () => setFormatacaoAberta(true) : null,
    editarCabecalhoRodape: () => { setEditandoCabecalho(true); toast.info('Editando cabeçalho e rodapé: vale para todas as páginas. Clique no texto da peça para voltar.'); },
    abrirLateral, verificarOrtografia,
    revisarComIA: () => { if (!somenteLeitura) void revisar(); },
    revisandoIA: iaEstado === 'revisando',
    // Função: só a barra Revisar (aba aberta) pergunta, e lê o texto inteiro ao desenhar.
    creditosRevisaoIA: creditosIA ? () => creditosIA(editor.getText().trim().length) : null,
    definirOrtografia: (ativa) => editor.commands.definirOrtografia(ativa),
    definirCorretorNativo: setCorretorNativo,
    dicionarioPessoal: dicionario,
    importarDicionario: async (f) => {
      const palavras = (await f.text()).split(/[\r\n;,]+/).map((s) => s.replace(/\/.*$/, '').trim()).filter(Boolean);
      mudarDicionario(Array.from(new Set([...dicionario, ...palavras])));
      toast.success(`${palavras.length} palavras importadas de ${f.name}.`);
    },
    documento, mudarDocumento,
    temIA: !!revisarIA, temOrtografiaEmbutida: !!ortografia,
    salvamento: onSalvar ? { salvar: salvarRef.current, situacao: situacaoSalvo, intervalo, definirIntervalo } : null,
    arquivo: acoesArquivo(),
    estilos: {
      personalizados: estilosPersonalizados,
      rotulo: rotuloEstilosPersonalizados,
      editaveis: !!onSalvarEstilo,
      criar: () => setCriandoEstilo(true),
      gerenciar: () => setGerenciandoEstilos(true),
      // "Atualizar X para corresponder": o parágrafo vira o estilo; os outros no mesmo estilo mudam junto.
      atualizar: () => {
        const atual = estado.estilo;
        const p = estiloPersonalizado(atual);
        if (p) void Promise.resolve(onSalvarEstilo?.({ ...p, ajustes: diferencaDeFormato(formatoDoPronto(p.baseadoEm), estado.formato) }));
        else if (atual in ESTILOS && onAjustarEstiloPronto) {
          if (!podeEditarEstilosDoEscritorio) {
            toast.info('Os estilos prontos são do escritório: quem o administra os atualiza. O seu Corpo da peça vem da Formatação do texto (Só para mim).');
            return;
          }
          // Só o que mudou agora vai para o do escritório: o "só para mim" gravado
          // na peça (por baixo) não vira de todos.
          const nome = atual as NomeEstilo;
          const base = formatoDoPronto(nome, false);
          const novo = { ...base, ...(ajustesDosEstilosProntos[nome] ?? {}), ...diferencaDeFormato(formatoDoPronto(nome), estado.formato) };
          // O campo que a peça ajusta fica explícito mesmo no valor padrão: senão o
          // "só para mim" dela, por baixo, voltaria a valer no parágrafo.
          const daPeca = ajustesDaPeca[nome] ?? {};
          const d = Object.fromEntries(
            (Object.keys(novo) as (keyof FormatoParagrafo)[]).filter((k) => novo[k] !== base[k] || k in daPeca).map((k) => [k, novo[k]]),
          ) as Partial<FormatoParagrafo>;
          void Promise.resolve(onAjustarEstiloPronto(nome, Object.keys(d).length ? d : null, 'escritorio'));
        }
        editor.chain().focus().definirEstilo(atual).run();
        toast.operation(`“${nomeDoEstilo(atual)}” atualizado.`);
      },
    },
    inserirLink: props.onRequestLink
      ? async () => {
          const url = await props.onRequestLink!();
          if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
      : undefined,
  };

  const sugestoes = sugestoesNoEstado(editor.state);
  const focoIA = CHAVE_IA.getState(editor.state)?.foco ?? null;
  const b = buscaNoEstado(editor.state);
  const largura = larguraDoPapel(documento);
  // "Ajustar à largura" alterna: ajustada, o mesmo botão volta a 100%. Derivado da
  // largura medida, não guardado — vale depois de recarregar e de redimensionar.
  // Para baixo: arredondar para cima punha a folha um pouco além da área.
  const zoomDaLargura = larguraMesa > 0
    ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.floor(((larguraMesa - 24) / (largura * PX_POR_CM)) * 10) / 10))
    : null;
  const ajustadaALargura = zoomDaLargura !== null && Math.abs(zoom - zoomDaLargura) < 0.001;
  const alternarAjusteALargura = () => { if (zoomDaLargura !== null) guardarZoom(ajustadaALargura ? 1 : zoomDaLargura); };
  const total = totalDePaginas(editor.state.doc);

  const extras = extrasBarra?.((t) => { editor.chain().focus().insertContent(t).run(); });
  const botaoTelaCheia = (
    <Botao
      dica={{ nome: telaCheia ? 'Sair da tela cheia' : 'Tela cheia', desc: telaCheia ? undefined : (telaCheiaDoApp?.descricao ?? 'O editor ocupa a janela inteira.') }}
      icone={telaCheia ? <Minimize2 className="cm-ef-ico" aria-hidden /> : <Maximize2 className="cm-ef-ico" aria-hidden />}
      ativo={telaCheia}
      className="cm-ef-tela-cheia-bt"
      onClick={alternarTelaCheia}
    />
  );

  /* ---------- Modo simples ---------- */
  if (modo === 'simples') {
    return (
      <TooltipProvider delayDuration={300}>
      <div ref={raiz} className={`cm-ef cm-ef-simples${className ? ` ${className}` : ''}`} style={style}>
        <div className="cm-ef-topo">{celular ? <BarraCelular ed={editor} est={estado} ac={acoes} aoAbrir={setGaveta} /> : <BarraInicio ed={editor} est={estado} ac={acoes} />}{extras && <div className="cm-ef-extras">{extras}</div>}</div>
        <EditorContent editor={editor} className="cm-ef-area-simples" style={{ minHeight: alturaMinima }} />
        <Gavetas gaveta={gaveta} ed={editor} est={estado} ac={acoes} aoFechar={() => setGaveta(null)} />
      </div>
      </TooltipProvider>
    );
  }

  /* ---------- Modo completo ---------- */
  const fecharLateral = () => { setLateral(null); setComparando(null); };

  // Comparação: a versão escolhida contra o texto atual (ou a anterior a ela contra ela).
  const versaoComparada = comparando ? versoes.find((v) => v.id === comparando.id) ?? null : null;
  const ordemVersoes = [...versoes].sort((a, b) => a.criadaEm.localeCompare(b.criadaEm));
  const anterior = versaoComparada ? ordemVersoes[ordemVersoes.findIndex((v) => v.id === versaoComparada.id) - 1] ?? null : null;
  const htmlDa = (v: VersaoDoTexto | null) => (v ? (v.html ?? htmlsCarregados[v.id]) : undefined);
  const htmlComparado = htmlDa(versaoComparada);
  const htmlAnterior = comparando?.contra === 'anterior' ? htmlDa(anterior) : undefined;
  // Documento inerte: o HTML da versão não executa nada ao ser lido
  // (num <div> do documento vivo, um <img onerror> dispararia).
  const docDoHtml = (h: string) =>
    PMDOMParser.fromSchema(editor.schema).parse(new DOMParser().parseFromString(h, 'text/html').body);
  const blocosComparados = versaoComparada && htmlComparado !== undefined
    ? (comparando!.contra === 'anterior' && anterior
        ? (htmlAnterior !== undefined ? compararParaTela(docDoHtml(htmlAnterior), docDoHtml(htmlComparado), editor.schema) : null)
        : compararParaTela(docDoHtml(htmlComparado), editor.state.doc, editor.schema))
    : null;
  const voltarAVersao = (v: VersaoDoTexto) => {
    const html = htmlDa(v);
    if (html === undefined) return;
    guardarVersaoRef.current('restauracao', undefined);
    editor.chain().focus().setContent(html, { emitUpdate: true }).run();
    setComparando(null);
    toast.operation(`Texto de volta à versão de ${quandoDaVersao(v)}. O de antes ficou guardado.`, desfazer);
  };
  // Revisão por IA dentro do texto: o balão da sugestão em foco e a barra.
  const sugestaoEmFoco = focoIA ? sugestoes.find((s) => s.id === focoIA) ?? null : null;
  const elementoDoFoco = () => (focoIA ? editor.view.dom.querySelector<HTMLElement>(`[data-sugestao="${CSS.escape(focoIA)}"]`) : null);
  // `contextElement`: o floating-ui escuta a rolagem dos ancestrais dele (a mesa), e o
  // balão acompanha o trecho. Variável, não literal: o tipo do Radix não o declara.
  const ancoraDoBalao = {
    getBoundingClientRect: () => elementoDoFoco()?.getBoundingClientRect() ?? new DOMRect(),
    contextElement: elementoDoFoco() ?? undefined,
  };
  const cartaoEmFoco = sugestaoEmFoco && (
    <CartaoSugestaoIA ref={cartaoRef} sugestao={sugestaoEmFoco} somenteLeitura={somenteLeitura}
      aoAceitar={() => resolverEAvancar(sugestaoEmFoco.id, true)} aoRecusar={() => resolverEAvancar(sugestaoEmFoco.id, false)} aoFechar={fecharBalao} />
  );
  const barraRevisao = revisaoAberta && (
    <BarraRevisaoIA estado={iaEstado} erro={iaErro} sugestoes={[...sugestoes].sort((a, b) => a.from - b.from)} foco={focoIA} perdidas={perdidas}
      creditos={creditosIA ? creditosIA(plugins?.caracteres ?? 0) : null} somenteLeitura={somenteLeitura}
      aoAnterior={() => irParaSugestao(vizinhaPendente(-1)?.id ?? null)} aoProxima={() => irParaSugestao(vizinhaPendente(1)?.id ?? null)}
      aoRevisar={() => { if (!somenteLeitura) void revisar(); }} aoFechar={encerrarRevisao}
      aoAceitarTodas={() => {
        if (somenteLeitura) return;
        // Numa transação só: o Desfazer (e um Ctrl+Z) volta o lote inteiro.
        const antes = sugestoes.filter((s) => !s.resolvida).map((s) => s.id);
        editor.commands.aceitarTodasSugestoes();
        const depois = new Map(sugestoesNoEstado(editor.state).map((s) => [s.id, s.resolvida]));
        const aceitas = antes.filter((id) => depois.get(id) === 'aceita').length;
        const caducas = antes.length - aceitas;
        toast.operation(
          `${aceitas === 1 ? '1 sugestão aceita' : `${aceitas} sugestões aceitas`}${caducas ? `; ${caducas} sem efeito (o trecho mudou)` : ''}.`,
          desfazer,
        );
      }} />
  );
  const conteudoLateral = lateral === 'busca' ? (
    <Lateral titulo="Localizar e substituir" subtitulo="Em toda a peça." aoFechar={fecharLateral} rodape={<span>Ctrl+L abre em Localizar; Ctrl+U, em Substituir. Enter vai ao próximo.</span>}>
      <ConteudoBusca termo={busca.termo} novo={busca.novo} caixa={busca.caixa} inteira={busca.inteira} total={b?.achados.length ?? 0} atual={b?.atual ?? -1} focarSubstituir={focarSubstituir}
        aoMudar={(p) => setBusca((x) => ({ ...x, ...p }))}
        aoAnterior={() => editor.commands.irParaAchado(-1)} aoProximo={() => editor.commands.irParaAchado(1)}
        aoSubstituir={() => editor.commands.substituirAtual(busca.novo)}
        aoSubstituirTodos={() => { const n = b?.achados.length ?? 0; editor.commands.substituirTodos(busca.novo); toast.operation(`${n} ocorrências substituídas.`, desfazer); }} />
    </Lateral>
  ) : lateral === 'versoes' ? (
    <Lateral titulo="Versões" subtitulo="Clique numa versão para ver, na folha, o que mudou até agora." aoFechar={fecharLateral}
      rodape={<span>{props.versoes ? `Guardadas${ondeSalva ? ` ${ondeSalva}` : ''}.` : 'Guardadas só nesta visita: ao fechar a página, somem.'} Voltar a uma versão guarda antes o texto de agora.</span>}>
      <ConteudoVersoes versoes={versoes} selecionada={comparando?.id ?? null} somenteLeitura={somenteLeitura}
        podeExcluir={!somenteLeitura && (!props.versoes || !!onExcluirVersao)}
        aoSelecionar={(id) => setComparando(id ? { id, contra: 'atual' } : null)}
        aoNomear={(r) => { guardarVersaoRef.current('nomeada', r); toast.operation(`Versão “${r}” guardada.`); }}
        aoExcluir={(id) => {
          if (comparando?.id === id) setComparando(null);
          if (!props.versoes) setVersoesInternas((l) => l.filter((v) => v.id !== id));
          void Promise.resolve(onExcluirVersao?.(id));
          toast.operation('Versão excluída.');
        }} />
    </Lateral>
  ) : null;

  return (
    <FolhaContext.Provider value={ctxFolha}>
      <TooltipProvider delayDuration={300}>
      <div ref={raiz} className={`cm-ef${celular ? ' is-celular' : ''}${telaCheiaPropria ? ' is-tela-cheia' : ''}${className ? ` ${className}` : ''}`} style={style}>
        <div className="cm-ef-topo">
          {celular ? (
            <>
              <BarraCelular ed={editor} est={estado} ac={acoes} aoAbrir={setGaveta} />
              {extras && !acoesMais && <div className="cm-ef-extras">{extras}</div>}
            </>
          ) : (
            <>
              <Abas aba={abaEmUso} aoTrocar={clicarAba} noTabela={estado.noTabela} extras={extras} recolhida={recolhida} abasDoApp={props.abasDoApp} />
              {!recolhida && (
                <>
                  {abaEmUso === 'arquivo' && <BarraArquivo ac={acoes} />}
                  {abaEmUso === 'inicio' && <BarraInicio ed={editor} est={estado} ac={acoes} />}
                  {abaEmUso === 'paragrafo' && <BarraParagrafo est={estado} ac={acoes} />}
                  {abaEmUso === 'inserir' && <BarraInserir ed={editor} ac={acoes} />}
                  {abaEmUso === 'tabela' && <BarraTabela ed={editor} est={estado} />}
                  {abaEmUso === 'layout' && <BarraLayout ed={editor} ac={acoes} />}
                  {abaEmUso === 'revisar' && <BarraRevisar est={estado} ac={acoes} />}
                </>
              )}
            </>
          )}
          {/* Fora do condicional do celular: o mesmo lugar na árvore nos dois modos,
              para a barra do app não desmontar (e perder o estado) ao trocar. */}
          {props.abasDoApp?.map((a) => (
            <div key={a.chave} className="cm-ef-barra-app" hidden={!celular && (recolhida || abaEmUso !== `app:${a.chave}`)}>
              {a.barra}
            </div>
          ))}
        </div>

        <div className="cm-ef-area">
          <div className="cm-ef-coluna">
            {aviso && <div className="cm-ef-aviso">{aviso}</div>}
            {/* No celular o balão não cabe junto do trecho: cartão fixo no pé, por cima do aviso. */}
            {celular && cartaoEmFoco && <div className="cm-ef-aviso cm-ef-sug-cel">{cartaoEmFoco}</div>}
            {!celular && <Regua documento={documento} formato={estado.formato} aoAjustar={ajustar} zoom={zoom} />}
            <div ref={refMesa} className="cm-ef-mesa" onScroll={medirMesa}
              onClick={(e) => { if (editandoCabecalho && (e.target as HTMLElement).closest('.cm-ef-texto')) setEditandoCabecalho(false); }}>
              <div
                ref={folhaRef}
                className={`cm-ef-folha${editandoCabecalho ? ' is-editando-hf' : ''}${pincel ? ' is-pincel' : ''}`}
                style={{
                  width: `${largura}cm`,
                  // Ampliada, a folha passa da mesa e a mesa rola — como no Word.
                  ...(!celular && zoom !== 1 ? { zoom, maxWidth: 'none' } : {}),
                  padding: `1.1cm ${documento.margens.direita}cm 0.9cm ${documento.margens.esquerda}cm`,
                  ['--cm-ef-m-sup' as string]: `${Math.max(0, documento.margens.superior - 3)}cm`,
                  ['--cm-ef-m-esq' as string]: celular ? '14px' : `${documento.margens.esquerda}cm`,
                  ['--cm-ef-m-dir' as string]: celular ? '14px' : `${documento.margens.direita}cm`,
                  ['--cm-ef-colunas' as string]: documento.colunas,
                  ['--cm-ef-regra' as string]: documento.linhaEntreColunas ? '1px solid #C9C8CF' : 'none',
                }}
              >
                <Cabecalho indice={0} />
                {versaoComparada && blocosComparados && (
                  <FolhaComparacao blocos={blocosComparados} versao={versaoComparada} contra={comparando!.contra} temAnterior={!!anterior}
                    somenteLeitura={somenteLeitura} aoVoltar={() => voltarAVersao(versaoComparada)} aoFechar={() => setComparando(null)}
                    aoTrocarContra={(c) => setComparando({ id: versaoComparada.id, contra: c })} />
                )}
                {/* Escondido, não desmontado: a comparação é só leitura e o editor volta como estava. */}
                <EditorContent editor={editor} className="cm-ef-corpo" hidden={!!(versaoComparada && blocosComparados)} />
                <Rodape indice={total - 1} />
              </div>
            </div>
          </div>
          {!celular && conteudoLateral}
        </div>

        {barraRevisao}
        <div className="cm-ef-estado" aria-live="polite">
          {/* No celular, na ponta ESQUERDA: a direita fica sob o botão flutuante do app. */}
          {celular && botaoTelaCheia}
          {onSalvar && <span className={`cm-ef-salvo is-${situacaoSalvo}`} title={dicaSalvamento || undefined}>{textoSalvamento}</span>}
          <span><b>{nomeDoEstilo(estado.estilo)}{estado.modificado ? ' (modificado)' : ''}</b> · {estado.formato.fonte} {fmtNum(estado.formato.tamanho)} · entrelinha {fmtNum(estado.formato.entrelinha)}</span>
          {!celular && (
            <span className="cm-ef-estado-dir">
              <span>Página {total > 1 ? `1 a ${total}` : '1'} · {documento.papel.toUpperCase() === 'A4' ? 'A4' : documento.papel} {documento.orientacao} · margens {documento.nomeMargens}</span>
              <span className="cm-ef-zoom" role="group" aria-label="Zoom da folha">
                <Botao dica={{ nome: 'Diminuir zoom' }} icone={<ZoomOut className="cm-ef-ico" aria-hidden />} desativado={zoom <= ZOOM_MIN} onClick={() => guardarZoom(zoom - 0.1)} />
                <Botao dica={{ nome: 'Tamanho real', desc: 'Volta a folha a 100%.' }} rotulo={`${Math.round(zoom * 100)}%`} className="cm-ef-zoom-valor" onClick={() => guardarZoom(1)} />
                <Botao dica={{ nome: 'Aumentar zoom' }} icone={<ZoomIn className="cm-ef-ico" aria-hidden />} desativado={zoom >= ZOOM_MAX} onClick={() => guardarZoom(zoom + 0.1)} />
                <Botao
                  dica={ajustadaALargura ? { nome: 'Voltar a 100%', desc: 'A folha volta ao tamanho real.' } : { nome: 'Ajustar à largura', desc: 'A folha ocupa a largura da área de edição.' }}
                  icone={<MoveHorizontal className="cm-ef-ico" aria-hidden />}
                  ativo={ajustadaALargura}
                  onClick={alternarAjusteALargura}
                />
                {botaoTelaCheia}
              </span>
            </span>
          )}
        </div>

        {/* No celular os painéis viram folha cheia por cima. */}
        {celular && conteudoLateral && <div className="cm-ef-lateral-cel">{conteudoLateral}</div>}
        <Gavetas gaveta={gaveta} ed={editor} est={estado} ac={acoes} aoFechar={() => setGaveta(null)}
          extras={acoesMais} telaCheia={{ ativa: telaCheia, alternar: alternarTelaCheia }} />

        {onSalvarEstilo && (
          <>
            <PainelCriarEstilo aberto={criandoEstilo} formato={estado.formato} estiloAtual={estado.estilo}
              podeEscritorio={podeEditarEstilosDoEscritorio} rotuloEscritorio="Todo o escritório"
              aoFechar={() => setCriandoEstilo(false)}
              aoCriar={(e) => {
                const novo: EstiloPersonalizado = { ...e, id: `e${Date.now().toString(36)}`, criadoEm: new Date().toISOString() };
                setCriandoEstilo(false);
                // O registro precisa do estilo já agora, para o parágrafo passar a usá-lo.
                definirRegistroDeEstilos({ personalizados: [...estilosPersonalizados, novo], ajustesProntos: ajustesQueValemAqui });
                editor.chain().focus().definirEstilo(`${PREFIXO_PERSONALIZADO}${novo.id}`).run();
                void Promise.resolve(onSalvarEstilo(novo));
                toast.operation(`Estilo “${novo.nome}” criado.`);
              }} />
            <PainelGerenciarEstilos aberto={gerenciandoEstilos} personalizados={estilosPersonalizados} ajustesProntos={ajustesDosEstilosProntos}
              ajustesPessoais={ajustesPessoaisDosEstilosProntos} escritorioPrevalece={escritorioPrevalece}
              aoRemoverPessoal={(nome) => { void Promise.resolve(onAjustarEstiloPronto?.(nome, null, 'pessoal')); toast.operation(`O seu “${ESTILOS[nome].nome}” saiu: as próximas peças abrem com o do escritório.`); }}
              rotuloEscritorio={rotuloEstilosPersonalizados} podeEscritorio={podeEditarEstilosDoEscritorio}
              aoFechar={() => setGerenciandoEstilos(false)}
              aoSalvarPersonalizado={(e) => { void Promise.resolve(onSalvarEstilo(e)); toast.operation(`“${e.nome}” salvo.`); }}
              aoExcluirPersonalizado={(id) => { void Promise.resolve(onExcluirEstilo?.(id)); toast.operation('Estilo excluído. Os parágrafos nele mantêm a forma.'); }}
              aoAjustarPronto={(nome, ajustes) => { void Promise.resolve(onAjustarEstiloPronto?.(nome, ajustes)); toast.operation(ajustes ? `“${ESTILOS[nome].nome}” ajustado para o escritório.` : `“${ESTILOS[nome].nome}” voltou ao padrão.`); }}
              aoDuplicar={(e) => { void Promise.resolve(onSalvarEstilo({ ...e, id: `e${Date.now().toString(36)}`, criadoEm: new Date().toISOString() })); toast.operation('Estilo duplicado (só para você).'); }} />
          </>
        )}
        <PainelParagrafo aberto={paragrafoAberto} formato={estado.formato} nomeEstilo={nomeDoEstilo(estado.estilo)}
          aoFechar={() => setParagrafoAberto(false)} aoAplicar={(f) => { ajustar(f); setParagrafoAberto(false); toast.operation('Parágrafo atualizado.', desfazer); }} />
        <PainelMargens aberto={margensAberto} margens={documento.margens} aoFechar={() => setMargensAberto(false)}
          aoAplicar={(m) => { mudarDocumento({ margens: m, nomeMargens: 'personalizadas' }); setMargensAberto(false); }} />
        {extrairFormatacao && (
          <PainelFormatacao aberto={formatacaoAberta} extrair={extrairFormatacao}
            alcances={onAjustarEstiloPronto ? (props.alcancesDoModelo ?? (podeEditarEstilosDoEscritorio ? ['escritorio'] : [])) : []}
            escritorioAjustaOCorpo={escritorioPrevalece && !!ajustesDosEstilosProntos[ESTILO_PADRAO] && Object.keys(ajustesDosEstilosProntos[ESTILO_PADRAO]!).length > 0}
            timbrado={destinoDoTimbrado}
            timbradoAtual={timbrado} margens={documento.margens} numerada={documento.paginas.ativa}
            aoFechar={() => setFormatacaoAberta(false)} aoAplicar={aplicarFormatacao} />
        )}
        {/* Com o app (que passa os timbrados), só os modos que o servidor guarda. */}
        <PainelTimbrado aberto={timbradoAberto} timbrados={timbrados} emUso={documento.timbrado} modos={props.timbrados ? MODOS_DO_APP : undefined} aoFechar={() => setTimbradoAberto(false)}
          aoAplicar={(id, t) => {
            if (t) guardarTimbrado(t);
            mudarDocumento({ timbrado: id });
            setTimbradoAberto(false);
            toast.success(id ? 'Timbrado aplicado a todas as páginas.' : 'Peça sem timbrado.');
          }} />

        {/* Balão da sugestão da IA, preso ao trecho marcado: segue a rolagem e o zoom
            da folha e some quando o trecho sai da vista. */}
        {!celular && (
          <Popover open={!!cartaoEmFoco} onOpenChange={(aberto) => { if (!aberto) editor.commands.focarSugestao(null); }}>
            <PopoverAnchor virtualRef={{ current: ancoraDoBalao }} />
            <PopoverContent align="start" side="bottom" sideOffset={6} hideWhenDetached className="cm-ef-balao-ia z-[1500] w-80 p-0"
              onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}
              // Clique no próprio trecho (ou em outro marcado) não fecha: o clique abre o dele.
              onInteractOutside={(e) => { if ((e.target as HTMLElement | null)?.closest?.('[data-sugestao], .cm-ef-rev-barra')) e.preventDefault(); }}>
              {cartaoEmFoco}
            </PopoverContent>
          </Popover>
        )}

        {/* Sugestões de ortografia, ancoradas no ponto da palavra (âncora fixa de tamanho zero). */}
        <Popover open={!!orto} onOpenChange={(aberto) => { if (!aberto) setOrto(null); }}>
          <PopoverAnchor asChild>
            <span aria-hidden style={{ position: 'fixed', left: orto?.x ?? 0, top: orto?.y ?? 0, width: 0, height: 0, pointerEvents: 'none' }} />
          </PopoverAnchor>
          <PopoverContent align="start" className="cm-ef-menu z-[1500] w-60 p-0" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
            {orto && (
              <div className="cm-ef-menu-corpo" role="menu" onMouseDown={(e) => e.preventDefault()}>
                {(ortografia?.sugestoes(orto.palavra) ?? []).map((s) => (
                  <BotaoNu key={s} role="menuitem" className="cm-ef-op cm-ef-op-forte" onClick={() => { editor.chain().focus().insertContentAt({ from: orto.from, to: orto.to }, s).run(); setOrto(null); }}>{s}</BotaoNu>
                ))}
                {!(ortografia?.sugestoes(orto.palavra) ?? []).length && <p className="cm-ef-menu-texto">Sem sugestões para “{orto.palavra}”.</p>}
                <div className="cm-ef-menu-sep" />
                <BotaoNu role="menuitem" className="cm-ef-op" onClick={() => { ignoradas.current = [...ignoradas.current, orto.palavra]; editor.commands.reverificar(); setOrto(null); }}>Ignorar</BotaoNu>
                <BotaoNu role="menuitem" className="cm-ef-op" onClick={() => { mudarDicionario([...dicionario, orto.palavra]); setOrto(null); toast.operation(`“${orto.palavra}” acrescentada ao seu dicionário.`); }}>Adicionar ao dicionário</BotaoNu>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
      </TooltipProvider>
    </FolhaContext.Provider>
  );
});

export default EditorForense;
